import axios from "axios";
import config from "../../config/environment";
import {
  agentTriggerNeedsCreateTrigger,
  getBuiltInAppFormDataEndpoint,
  isAgentPollingTrigger,
  isBuiltInTriggerApp,
  resolveAgentTriggerPerformList,
} from "../../constants/triggerApps";
import {
  createWorkflowForAgentApi,
  updateWorkflowForAgentApi,
} from "../workflow/workflowSlice";
import {
  buildClonedAgentPayload,
  createDefaultAgent,
  createDefaultAction,
  createDefaultRule,
  createDefaultTrigger,
} from "./types";
import {
  catalogFieldsByName,
  coalesceExecuteFieldsForAction,
  normalizeExecuteFieldRow,
  resolveFieldIn,
  serializeExecuteFieldValue,
} from "./executeFieldNormalize";
import { RUN_KINDS, RUN_STEP_PHASES } from "./agentTaskTypes";
import {
  fetchAllCredentialsForApp,
  pickBestCredential,
} from "../credentials/fetchCredentialsForApp";
import { fetchAgentGetDDOptions } from "./agentGetDD";
import {
  billingPeriodQuery,
  isLifetimeTaskQuota,
  parseRunCreatedAtMs,
  resolvePlanQuota,
  resolveTaskUsage,
  runCreatedAtInPeriod,
} from "./billingPeriod";
import { runActionCount } from "./agentRunFormat";
import {
  normalizeCustomRequestHttpValue,
  shapeCustomRequestHttpValueForExecute,
} from "../../constants/customRequestAction";

const AGENTS_STORAGE_KEY = "workload_agents_v1";
const AGENT_RUNS_STORAGE_KEY = "workload_agent_runs_v1";

const getMode = () => (config.AGENTS_API_MODE || "http").toLowerCase();
/** Djangocaller executeAction for approved tools (default on; set REACT_APP_AGENTS_EXECUTE_VIA_HTTP=false to disable). */
const isExecuteViaHttp = () => config.AGENTS_EXECUTE_VIA_HTTP !== false;

const nowIso = () => new Date().toISOString();

const safeParse = (value, fallback) => {
  if (value === null || value === undefined || value === "") return fallback;
  try {
    const parsed = JSON.parse(value);
    return parsed === null || parsed === undefined ? fallback : parsed;
  } catch (err) {
    return fallback;
  }
};

const loadAgents = () => {
  const parsed = safeParse(localStorage.getItem(AGENTS_STORAGE_KEY), []);
  return Array.isArray(parsed) ? parsed : [];
};
const saveAgents = (agents) => localStorage.setItem(AGENTS_STORAGE_KEY, JSON.stringify(agents));

const loadRuns = () => {
  const parsed = safeParse(localStorage.getItem(AGENT_RUNS_STORAGE_KEY), {});
  return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
};
const saveRuns = (runsByAgent) => localStorage.setItem(AGENT_RUNS_STORAGE_KEY, JSON.stringify(runsByAgent));

const seedMockAgents = () => {
  const existing = loadAgents();
  if (existing.length > 0) return existing;
  const action = createDefaultAction();
  const seeded = createDefaultAgent({
    name: "Job Lifecycle Supervisor",
    description: "Monitors job lifecycle events and escalates missing follow-up activity.",
    goal: "Reduce missed dispatches and unpaid invoices.",
    triggers: [
      {
        ...createDefaultTrigger(),
        source: "workflow_event",
        type: "job_created",
      },
      {
        ...createDefaultTrigger(),
        source: "workflow_event",
        type: "invoice_closed",
      },
    ],
    actions: [{ ...action, type: "send_slack", label: "Notify Ops Channel" }],
    rules: [
      createDefaultRule(action.action_id),
      {
        ...createDefaultRule(action.action_id),
        name: "Alert when invoice is closed",
        if: "event.type == 'invoice_closed' && payload.balance_due > 0",
      },
    ],
  });
  saveAgents([seeded]);
  return [seeded];
};

const sortByUpdatedAt = (items) => {
  return [...items].sort((a, b) => {
    const aMs = new Date(a.updated_at || a.created_at || 0).getTime();
    const bMs = new Date(b.updated_at || b.created_at || 0).getTime();
    return bMs - aMs;
  });
};

const clone = (value) => JSON.parse(JSON.stringify(value));

/** Sanitize agent before save so we never persist the string "null" for tenant/user/credential/authname. */
const sanitizeAgentForSave = (agent) => {
  const next = { ...agent };
  if (next.tenant_id != null && (String(next.tenant_id) === "null" || String(next.tenant_id) === "undefined")) next.tenant_id = "";
  if (next.created_by_user_id != null && (String(next.created_by_user_id) === "null" || String(next.created_by_user_id) === "undefined")) next.created_by_user_id = "";
  if (Array.isArray(next.actions)) {
    next.actions = next.actions.map((a) => {
      const ac = { ...a };
      if (String(ac.credential) === "null" || String(ac.credential) === "undefined") ac.credential = "";
      if (String(ac.authname) === "null" || String(ac.authname) === "undefined") ac.authname = "";
      if (ac.config && typeof ac.config === "object") {
        ac.config = { ...ac.config };
        if (String(ac.config.credential) === "null" || String(ac.config.credential) === "undefined") ac.config.credential = "";
        if (String(ac.config.authname) === "null" || String(ac.config.authname) === "undefined") ac.config.authname = "";
      }
      return ac;
    });
  }
  if (Array.isArray(next.triggers)) {
    next.triggers = next.triggers.map((t) => {
      const tr = { ...t };
      if (String(tr.credential) === "null" || String(tr.credential) === "undefined") tr.credential = "";
      if (String(tr.authname) === "null" || String(tr.authname) === "undefined") tr.authname = "";
      return tr;
    });
  }
  return next;
};

/** Path for djangocaller (executeAction, createTrigger, getDD). Hosted on API Gateway, not Express agents. */
const DJANGOCALLER_PATH = config.DJANGOCALLER_PATH || "/djangocaller";
const DJANGOCALLER_API_BASE_URL =
  config.DJANGOCALLER_API_BASE_URL ||
  config.BACKEND_URL ||
  "https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production";

const isWorkloadBrowserAction = (data) => {
  const app = String(data?.app || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "-");
  const op = String(data?.operation || data?.action || data?.action_key || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "")
    .replace(/-/g, "")
    .replace(/\s/g, "");
  if (
    (app === "workload-browser" || app === "workloadbrowser") &&
    ["browseweb", "browse", "browseract", "runbrowser"].includes(op)
  ) {
    return true;
  }
  // Phase D Browser RPA on any third-party app
  return op === "browserrpa";
};

const isBrowserRpaAction = (data) => {
  const op = String(data?.operation || data?.action || data?.action_key || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "")
    .replace(/-/g, "")
    .replace(/\s/g, "");
  return op === "browserrpa";
};

/**
 * Preview/live Browser via Lambda Function URL (up to ~5 min).
 * Avoids djangocaller API Gateway's ~29s "Endpoint request timed out".
 */
const executeBrowserViaFunctionUrl = async (data) => {
  const fnUrl = (
    config.AGENT_BROWSER_ACT_FUNCTION_URL ||
    config.AGENT_FILE_EXTRACT_FUNCTION_URL ||
    ""
  )
    .trim()
    .replace(/\/$/, "");
  if (!fnUrl) {
    throw new Error("No AGENT_FILE_EXTRACT_FUNCTION_URL configured for Browser");
  }
  const isRpa = isBrowserRpaAction(data);
  const payload = isRpa
    ? {
        operation: "browserRpa",
        tool: "browser_rpa",
        fields: Array.isArray(data?.fields) ? data.fields : [],
        credential: data?.credential || "",
        tenant_id: data?.tenant_id || "",
        user_id: data?.user_id || "",
        app: data?.app || "",
        stop_session: data?.stop_session !== false && data?.keepSession !== true,
      }
    : {
        operation: "browserAct",
        tool: "browser_act",
        fields: Array.isArray(data?.fields) ? data.fields : [],
      };
  try {
    const response = await axios.post(fnUrl, payload, {
      // RPA budget: session start (≤40s) + CDP (≤20s) + login assist (~10s) +
      // Live View mint after CDP release. Backend must hand off well before this.
      timeout: isRpa ? 120000 : 300000,
      headers: { "Content-Type": "application/json" },
    });
    let body = response?.data;
    if (body && typeof body.body === "string" && body.ok === undefined && body.sessionId === undefined) {
      try {
        body = JSON.parse(body.body);
      } catch (_) {
        /* keep */
      }
    } else if (body && body.body && typeof body.body === "object" && body.ok === undefined) {
      body = body.body;
    }
    if (!body || typeof body !== "object") {
      throw new Error("Browser Function URL returned invalid JSON");
    }
    return body;
  } catch (err) {
    const msg = String(err?.message || err || "").toLowerCase();
    const timedOut =
      err?.code === "ECONNABORTED" ||
      msg.includes("timeout") ||
      msg.includes("timed out") ||
      err?.response?.status === 504;
    if (timedOut) {
      // browseWeb only: sweep orphans. Never sweep wl-rpa-* on timeout — that
      // kills the session Lambda is about to hand to Live View.
      if (!isRpa) {
        try {
          await axios.post(
            fnUrl,
            {
              operation: "browser_session_stop",
              tool: "stop_ready_browser_sessions",
              namePrefix: "wl-",
              maxStop: 15,
            },
            { timeout: 60000, headers: { "Content-Type": "application/json" } },
          );
        } catch (sweepErr) {
          console.warn("browser timeout orphan sweep failed", sweepErr);
        }
      }
      if (isRpa) {
        const e = new Error(
          "Browser RPA timed out before Live View handoff. Retry with an empty sessionId, or redeploy the Browser Function URL Lambda with the latest agentcore_tools (CDP must release before take_control).",
        );
        e.code = err?.code || "ECONNABORTED";
        e.cause = err;
        throw e;
      }
    }
    throw err;
  }
};

/**
 * Best-effort stop AgentCore browser sessions after preview/approved tools.
 * Skips sessions that still need Live View (needsUserAction) — including the
 * same sessionId nested under task/login payloads (those lack needsUserAction).
 */
export const stopBrowserSessions = async (sessionIds = []) => {
  const ids = [
    ...new Set(
      (Array.isArray(sessionIds) ? sessionIds : [sessionIds])
        .map((s) => String(s || "").trim())
        .filter(Boolean),
    ),
  ];
  if (!ids.length) return { ok: true, stopped: [], note: "no_session_ids" };
  const fnUrl = (
    config.AGENT_BROWSER_ACT_FUNCTION_URL ||
    config.AGENT_FILE_EXTRACT_FUNCTION_URL ||
    ""
  )
    .trim()
    .replace(/\/$/, "");
  if (!fnUrl) {
    return { ok: false, error: "no_function_url", failed: ids.map((s) => ({ sessionId: s })) };
  }
  try {
    const response = await axios.post(
      fnUrl,
      {
        operation: "browser_session_stop",
        tool: "browser_session_stop",
        sessionIds: ids,
      },
      { timeout: 60000, headers: { "Content-Type": "application/json" } },
    );
    let body = response?.data;
    if (body && typeof body.body === "string") {
      try {
        body = JSON.parse(body.body);
      } catch (_) {
        /* keep */
      }
    } else if (body && body.body && typeof body.body === "object") {
      body = body.body;
    }
    return body && typeof body === "object" ? body : { ok: true, raw: body };
  } catch (err) {
    return { ok: false, error: err?.message || "stop_failed", sessionIds: ids };
  }
};

const harvestBrowserSessionIdsFromResults = (results) => {
  const keepIds = new Set();
  const candidateIds = [];

  const noteKeep = (node) => {
    if (!node || typeof node !== "object") return;
    if (Array.isArray(node)) {
      node.slice(0, 40).forEach(noteKeep);
      return;
    }
    if (node.needsUserAction || node.liveViewUrl) {
      const sid = String(node.sessionId || node.session_id || "").trim();
      if (sid) keepIds.add(sid);
    }
    // Shallow scan common nests only (avoid huge walks for keep set).
    ["data", "task", "login", "result"].forEach((k) => {
      if (node[k] && typeof node[k] === "object") noteKeep(node[k]);
    });
  };

  const walk = (node, depth = 0, ancestorKeep = false) => {
    if (depth > 8 || node == null) return;
    if (Array.isArray(node)) {
      node.slice(0, 40).forEach((x) => walk(x, depth + 1, ancestorKeep));
      return;
    }
    if (typeof node !== "object") return;
    const sid = String(node.sessionId || node.session_id || "").trim();
    const keepLive =
      ancestorKeep ||
      Boolean(node.needsUserAction) ||
      Boolean(node.liveViewUrl) ||
      (sid && keepIds.has(sid));
    const src = String(node.source || "").toLowerCase();
    const looksBrowser =
      src.includes("browser") ||
      Boolean(node.liveViewUrl) ||
      Boolean(node.browserIdentifier) ||
      Array.isArray(node.events) ||
      Boolean(node.loginRecipeVerified);
    if (sid && keepLive) {
      keepIds.add(sid);
    } else if (sid && !keepLive && (looksBrowser || depth <= 1)) {
      candidateIds.push(sid);
    }
    Object.values(node).forEach((v) => walk(v, depth + 1, keepLive));
  };

  (Array.isArray(results) ? results : []).forEach((r) => {
    // Protect Live View sessions before harvesting anything to stop.
    if (r?.needsUserAction || r?.liveViewUrl || r?.data?.needsUserAction || r?.data?.liveViewUrl) {
      const sid = String(r?.sessionId || r?.data?.sessionId || r?.data?.session_id || "").trim();
      if (sid) keepIds.add(sid);
    }
    noteKeep(r);
    noteKeep(r?.data);
    walk(r?.data, 0, Boolean(r?.needsUserAction || r?.data?.needsUserAction));
    walk(r?.error, 0, false);
    if (r?.sessionId && !r?.needsUserAction && !keepIds.has(String(r.sessionId).trim())) {
      candidateIds.push(String(r.sessionId).trim());
    }
  });

  return [...new Set(candidateIds.filter((id) => id && !keepIds.has(id)))];
};

/**
 * Resume Browser RPA after user finishes MFA/CAPTCHA in Live View.
 * Pass the original Browser Task goal/actions so work continues on the SAME session.
 */
export const resumeBrowserRpa = async ({
  sessionId,
  profileId = "",
  loginRecipe = null,
  actions = null,
  goal = "",
  app = "",
  tenantId = "",
  userId = "",
  stopSession = true,
} = {}) => {
  const fnUrl = (
    config.AGENT_BROWSER_ACT_FUNCTION_URL ||
    config.AGENT_FILE_EXTRACT_FUNCTION_URL ||
    ""
  )
    .trim()
    .replace(/\/$/, "");
  if (!fnUrl) {
    throw new Error("No Function URL configured for Browser RPA resume");
  }
  const payload = {
    operation: "browserRpaResume",
    tool: "browser_rpa_resume",
    sessionId,
    profileId,
    loginRecipe: loginRecipe && typeof loginRecipe === "object" ? loginRecipe : undefined,
    actions: Array.isArray(actions) ? actions : undefined,
    goal: goal || "",
    app: app || "",
    tenant_id: tenantId || "",
    user_id: userId || "",
    saveProfile: true,
    stopSession: stopSession !== false,
  };
  const response = await axios.post(fnUrl, payload, {
    timeout: 300000,
    headers: { "Content-Type": "application/json" },
  });
  let body = response?.data;
  if (body && typeof body.body === "string" && body.ok === undefined) {
    try {
      body = JSON.parse(body.body);
    } catch (_) {
      /* keep */
    }
  } else if (body && body.body && typeof body.body === "object" && body.ok === undefined) {
    body = body.body;
  }
  return body && typeof body === "object" ? body : { ok: false, error: "invalid_resume_response" };
};

/** Pull Browser Task goal/actions from an agent action config / proposed fields. */
export const extractBrowserRpaTask = (action = {}, proposed = null) => {
  let goal = "";
  let actions = null;
  const fieldLists = [
    proposed?.fields,
    action?.config?.fields,
    action?.fields,
  ].filter((x) => Array.isArray(x));
  for (const list of fieldLists) {
    for (const f of list) {
      const name = String(f?.name || f?.title || "").trim().toLowerCase();
      const val = f?.value;
      if (name === "goal" || name === "intent") {
        if (val != null && String(val).trim()) goal = String(val).trim();
      }
      if (
        name === "browser task" ||
        name === "browsertask" ||
        name === "task" ||
        name === "browser_task"
      ) {
        if (val && typeof val === "object") {
          if (Array.isArray(val.actions)) actions = val.actions;
          if (val.goal && !goal) goal = String(val.goal).trim();
        } else if (typeof val === "string" && val.trim().startsWith("{")) {
          try {
            const parsed = JSON.parse(val);
            if (parsed && typeof parsed === "object") {
              if (Array.isArray(parsed.actions)) actions = parsed.actions;
              if (parsed.goal && !goal) goal = String(parsed.goal).trim();
            }
          } catch (_) {
            /* ignore */
          }
        } else if (typeof val === "string" && val.trim() && !goal) {
          goal = val.trim();
        }
      }
    }
  }
  if (!goal && typeof proposed?.goal === "string") goal = proposed.goal.trim();
  return { goal, actions };
};

/** Latest reusable AgentCore session from prior preview steps. */
export const findReusableBrowserSession = (priorSteps = []) => {
  if (!Array.isArray(priorSteps)) return null;
  for (let i = priorSteps.length - 1; i >= 0; i -= 1) {
    const out = priorSteps[i]?.output;
    if (!out || typeof out !== "object") continue;
    const sid = String(out.sessionId || out.session_id || "").trim();
    if (!sid) continue;
    if (out.sessionStopped && !out.keepSession) continue;
    if (out.loggedIn || out.loginRecipeVerified || out.resumed || out.continuedFromLiveView) {
      return {
        sessionId: sid,
        profileId: String(out.profileId || out.profile_id || "").trim(),
        loginRecipe:
          out.loginRecipe && typeof out.loginRecipe === "object" ? out.loginRecipe : null,
      };
    }
  }
  return null;
};

/**
 * Ensure Phase D apps have basic username/password authInfo on live Mongo
 * so /auth/:slug Sign in works (no public API / HTTP testAuth required).
 */
export const ensureBrowserLoginAuth = async ({ app, loginUrl = "" } = {}) => {
  const slug = String(app || "").trim();
  if (!slug) return { ok: false, error: "missing_app" };
  const fnUrl = (
    config.AGENT_BROWSER_ACT_FUNCTION_URL ||
    config.AGENT_FILE_EXTRACT_FUNCTION_URL ||
    ""
  )
    .trim()
    .replace(/\/$/, "");
  if (!fnUrl) {
    return { ok: false, error: "no_function_url" };
  }
  try {
    const response = await axios.post(
      fnUrl,
      {
        operation: "ensureBrowserLoginAuth",
        tool: "ensure_browser_login_auth",
        app: slug,
        loginUrl: loginUrl || "",
      },
      { timeout: 60000, headers: { "Content-Type": "application/json" } },
    );
    let body = response?.data;
    if (body && typeof body.body === "string" && body.ok === undefined) {
      try {
        body = JSON.parse(body.body);
      } catch (_) {
        /* keep */
      }
    } else if (body && body.body && typeof body.body === "object" && body.ok === undefined) {
      body = body.body;
    }
    return body && typeof body === "object" ? body : { ok: false, error: "invalid_response" };
  } catch (err) {
    return {
      ok: false,
      error: err?.response?.data?.error || err?.message || "ensure_browser_login_auth_failed",
    };
  }
};

/** Best-effort Mongo step log via djangocaller (fast; must not wait on Browser). */
const logBrowserStepViaDjangocaller = async (data, result) => {
  try {
    await djangocallerHttpClient.post(
      DJANGOCALLER_PATH,
      {
        operation: "appendAgentBrowserStep",
        data: {
          ...data,
          browser_result: result,
        },
      },
      { timeout: 20000 },
    );
  } catch (err) {
    console.warn(
      "appendAgentBrowserStep skipped:",
      err?.response?.data || err?.message || err,
    );
  }
};

/** Agent CRUD (Express :5000 or legacy). */
const httpClient = axios.create({
  baseURL: config.AGENTS_API_BASE_URL || config.BACKEND_URL,
  headers: { "Content-Type": "application/json" },
});

httpClient.interceptors.request.use((req) => {
  try {
    req.headers = req.headers || {};
    const tok = localStorage.getItem("tok_");
    if (tok != null && String(tok).trim() !== "") {
      req.headers.Authorization = `Bearer ${String(tok).trim()}`;
    }
    const tenant = localStorage.getItem("tenant");
    const user = localStorage.getItem("user");
    // Allow per-request X-Tenant-Id (e.g. clone into another workspace).
    const existingTenant = req.headers["X-Tenant-Id"] ?? req.headers["x-tenant-id"];
    if (
      (existingTenant == null || String(existingTenant).trim() === "") &&
      tenant != null &&
      String(tenant).trim() !== ""
    ) {
      req.headers["X-Tenant-Id"] = String(tenant).trim();
    }
    if (user != null && String(user).trim() !== "") {
      req.headers["X-User-Id"] = String(user).trim();
    }
  } catch (_) {
    /* ignore */
  }
  return req;
});

/** Djangocaller Lambda/Django — separate from REACT_APP_AGENTS_API_BASE_URL. No Bearer tenant headers. */
const djangocallerHttpClient = axios.create({
  baseURL: DJANGOCALLER_API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

/**
 * Express/Mongo often returns `{ agent }`, `{ data }`, or only `{ _id }` on PATCH.
 * Preview calls save first; replacing React state with that sparse body clears the form and validation.
 */
function normalizeAgentFromApi(raw) {
  if (raw == null) return null;
  let data = raw;
  if (typeof data === "object" && !Array.isArray(data)) {
    if (data.agent && typeof data.agent === "object" && !Array.isArray(data.agent)) data = data.agent;
    else if (data.document && typeof data.document === "object") data = data.document;
    else if (data.data && typeof data.data === "object" && !Array.isArray(data.data)) data = data.data;
  }
  if (typeof data !== "object" || data === null || Array.isArray(data)) return null;
  const out = { ...data };
  if (out.agent_id == null && out._id != null) out.agent_id = String(out._id);
  if (!Array.isArray(out.triggers)) out.triggers = [];
  if (!Array.isArray(out.actions)) out.actions = [];
  if (!Array.isArray(out.rules)) out.rules = [];
  if (out.policy == null || typeof out.policy !== "object") out.policy = {};
  return out;
}

function normalizeAgentListPayload(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "object") {
    if (Array.isArray(raw.agents)) return raw.agents;
    if (Array.isArray(raw.data)) return raw.data;
    if (Array.isArray(raw.items)) return raw.items;
    if (Array.isArray(raw.results)) return raw.results;
  }
  return [];
}

async function refetchAgentRecord(agentId) {
  if (agentId == null || String(agentId).trim() === "") return null;
  const r = await httpClient.get(`/agents/${agentId}`).catch(() => null);
  return normalizeAgentFromApi(r?.data ?? null);
}

function normalizeRunFromApi(raw) {
  if (raw == null) return null;
  let d = raw;
  if (typeof d === "object" && d.run && typeof d.run === "object") d = d.run;
  if (typeof d === "object" && d.data && typeof d.data === "object" && (d.data.run_id || d.data.task_id)) d = d.data;
  if (typeof d !== "object" || d === null) return null;
  const out = { ...d };
  if (out.run_id == null && out.task_id != null) out.run_id = String(out.task_id);
  if (out.run_id == null && out._id != null) out.run_id = String(out._id);
  return out;
}

function normalizeRunListPayload(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "object") {
    if (Array.isArray(raw.runs)) return raw.runs;
    if (Array.isArray(raw.tasks)) return raw.tasks;
    if (Array.isArray(raw.data)) return raw.data;
    if (Array.isArray(raw.items)) return raw.items;
  }
  return [];
}

/**
 * Paginated GET /agents/:agentId/runs — body is { runs, total } or legacy array.
 * Optional live_total: count of run_kind === live matching the same filter (quota banner).
 */
function normalizeRunListPageResponse(raw) {
  let runs = [];
  let total = 0;
  let liveTotal = null;
  if (raw == null) return { runs: [], total: 0, liveTotal: null };
  if (Array.isArray(raw)) {
    runs = raw;
    total = runs.length;
  } else if (typeof raw === "object") {
    const arr =
      (Array.isArray(raw.runs) && raw.runs) ||
      (Array.isArray(raw.tasks) && raw.tasks) ||
      (Array.isArray(raw.data) && raw.data) ||
      (Array.isArray(raw.items) && raw.items) ||
      [];
    runs = arr;
    const t = raw.total ?? raw.count ?? raw.totalCount;
    total = Number(t);
    if (!Number.isFinite(total)) total = runs.length;
    if (raw.live_total != null) liveTotal = Number(raw.live_total);
    else if (raw.liveTotal != null) liveTotal = Number(raw.liveTotal);
    if (liveTotal != null && !Number.isFinite(liveTotal)) liveTotal = null;
  }
  return {
    runs: runs.map((x) => normalizeRunFromApi(x)).filter(Boolean),
    total,
    liveTotal,
  };
}

function unwrapTenantDocument(raw) {
  if (!raw || typeof raw !== "object") return null;
  if (raw.tenant && typeof raw.tenant === "object") return raw.tenant;
  if (raw.data && typeof raw.data === "object" && (raw.data.creditQuota != null || raw.data.plan)) {
    return raw.data;
  }
  return raw;
}

export async function fetchTenantPlan() {
  const tenantId =
    typeof localStorage !== "undefined" ? localStorage.getItem("tenant") : null;
  const token = typeof localStorage !== "undefined" ? localStorage.getItem("tok_") : null;
  if (!tenantId || tenantId === "null" || tenantId === "undefined") return null;
  try {
    const res = await axios.get(`https://backend.workload.co/api/tenant/${tenantId}`, {
      timeout: 8000,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${String(token).trim()}` } : {}),
      },
    });
    return unwrapTenantDocument(res.data);
  } catch {
    return null;
  }
}

function buildUsageSummary({ tenant, liveTotal, byAgent, period }) {
  const lifetime = isLifetimeTaskQuota(tenant);
  const used = lifetime ? resolveTaskUsage(tenant) : liveTotal;
  return {
    live_total: used,
    liveTotal: used,
    quota: resolvePlanQuota(tenant),
    lifetime,
    creditUsage: Number(tenant?.creditUsage) || 0,
    lifetimeCreditUsage: Number(tenant?.lifetimeCreditUsage) || 0,
    tenant,
    by_agent: byAgent,
    periodStart: lifetime ? null : period.periodStart?.toISOString?.() || period.periodStart,
    periodEnd: lifetime ? null : period.periodEnd?.toISOString?.() || period.periodEnd,
    dateFrom: lifetime ? null : period.dateFrom,
    dateTo: lifetime ? null : period.dateTo,
    daysUntilReset: lifetime ? null : period.daysUntilReset,
    agent_live_total: liveTotal,
  };
}

/** Axios query params for GET /agents/:id/runs (snake_case for Express). */
function buildAgentRunsQueryParams(query) {
  if (!query || typeof query !== "object") return {};
  const params = {};
  if (Number.isFinite(query.limit) && query.limit > 0) params.limit = query.limit;
  if (Number.isFinite(query.skip) && query.skip >= 0) params.skip = query.skip;
  if (query.status && String(query.status).trim() && query.status !== "all") {
    params.status = String(query.status).trim();
  }
  if (query.q != null && String(query.q).trim()) params.q = String(query.q).trim();
  if (query.dateFrom != null && String(query.dateFrom).trim()) params.date_from = String(query.dateFrom).trim();
  if (query.dateTo != null && String(query.dateTo).trim()) params.date_to = String(query.dateTo).trim();
  if (query.app && String(query.app).trim() && query.app !== "all") params.app = String(query.app).trim();
  return params;
}

const _matchesRunDeepQuery = (run, rawQuery) => {
  const q = String(rawQuery || "").trim().toLowerCase();
  if (!q) return true;
  if (!run || typeof run !== "object") return false;
  try {
    // Include steps/outcomes so users can search for IDs inside tool responses (e.g. appointmentNumber).
    const blob = JSON.stringify(
      {
        run_id: run.run_id,
        task_id: run.task_id,
        status: run.status,
        summary: run.summary,
        run_kind: run.run_kind,
        error: run.error,
        input_event: run.input_event,
        steps: run.steps,
        actions_taken: run.actions_taken,
      },
      null,
      0,
    )
      .toLowerCase()
      .slice(0, 900000); // keep bounded for huge outcomes
    return blob.includes(q);
  } catch {
    const rid = String(run.run_id || run.task_id || "").toLowerCase();
    return rid.includes(q);
  }
};

const _clientFilterRunsPage = (runs, query) => {
  let out = Array.isArray(runs) ? runs.slice() : [];
  const status = query?.status;
  if (status && status !== "all") {
    out = out.filter((r) => String(r?.status || "").toLowerCase() === String(status).toLowerCase());
  }
  if (query?.dateFrom) {
    const t0 = new Date(`${query.dateFrom}T00:00:00`).getTime();
    out = out.filter((r) => (r?.created_at ? new Date(r.created_at).getTime() : 0) >= t0);
  }
  if (query?.dateTo) {
    const t1 = new Date(`${query.dateTo}T23:59:59.999`).getTime();
    out = out.filter((r) => (r?.created_at ? new Date(r.created_at).getTime() : 0) <= t1);
  }
  if (query?.app && query.app !== "all") {
    const want = String(query.app).toLowerCase();
    out = out.filter((run) => {
      const steps = run?.steps || [];
      return Array.isArray(steps) && steps.some((s) => String(s?.app_label || s?.app || "").toLowerCase() === want);
    });
  }
  if (query?.q != null && String(query.q).trim()) {
    out = out.filter((run) => _matchesRunDeepQuery(run, query.q));
  }
  return out;
};

/** Extract primitive from trigger field value (supports { value: x } and nested). */
const extractTriggerFieldValue = (v) => {
  let cur = v;
  while (cur != null && typeof cur === "object" && !Array.isArray(cur) && "value" in cur) {
    cur = cur.value;
  }
  return cur;
};

/**
 * Fetch sample trigger data for agent preview so preview closely mimics runtime.
 * - Schedule: createTrigger → response is testData (same as TriggerPanel: testData = triggerMessage).
 * - workload-forms / webhook (built-in): createTrigger → use response as testData if object.
 * - Polling / third-party webhook: getDD on performList → first item in returned array is testData.
 */
/** Build a fallback schedule payload from trigger.fields so the brain gets non-empty data when createTrigger fails. */
const buildScheduleFallbackPayload = (trigger) => {
  const fields = trigger?.fields || [];
  const payload = {
    schedule: "scheduled",
    source: "schedule",
    _preview_fallback: true,
  };
  fields.forEach((f) => {
    const name = f?.name || f?.title;
    if (!name) return;
    const val = f?.value != null ? extractTriggerFieldValue(f.value) : f?.value;
    if (val !== undefined && val !== null) payload[name] = val;
  });
  if (!payload.schedule_preset && !payload.cron) payload.schedule_preset = "every_30_minutes";
  return payload;
};

/**
 * Schedule trigger uses RJSF-style schema: "Every Month" requires "Day of Month" + "Time of Day",
 * "Every Week" requires "Day of Week" + "Time of Day", "Every Day" requires "Time of Day".
 * Normalize freeform times/days into the exact enum strings the Schedule Lambda expects.
 * Prefer filling missing required siblings over coercing to Custom Interval (which silently
 * changes a daily/weekly/monthly ask into a 30-minute poll).
 */
const SCHEDULE_TIME_OF_DAY = [
  "Midnight", "1am", "2am", "3am", "4am", "5am", "6am", "7am", "8am", "9am", "10am", "11am",
  "Noon", "1pm", "2pm", "3pm", "4pm", "5pm", "6pm", "7pm", "8pm", "9pm", "10pm", "11pm",
];
const SCHEDULE_DAYS_OF_WEEK = [
  "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday",
];

const normalizeScheduleTimeOfDay = (raw) => {
  if (raw == null || raw === "") return null;
  const s = String(raw).trim();
  if (SCHEDULE_TIME_OF_DAY.includes(s)) return s;
  const low = s.toLowerCase().replace(/[\s.]/g, "");
  if (["midnight", "12am", "12:00am", "00:00", "0:00", "0000"].includes(low)) return "Midnight";
  if (["noon", "12pm", "12:00pm", "12:00"].includes(low)) return "Noon";
  const m = s.match(/^(\d{1,2})(?::(\d{2}))?\s*(a\.?m\.?|p\.?m\.?)?$/i)
    || low.match(/^(\d{1,2})(?::(\d{2}))?(am|pm)$/i);
  if (!m) return null;
  let hour = parseInt(m[1], 10);
  const meridiem = (m[3] || "").toLowerCase().replace(/\./g, "");
  if (!meridiem) {
    if (hour === 0) return "Midnight";
    if (hour === 12) return "Noon";
    if (hour >= 1 && hour <= 11) return `${hour}am`;
    if (hour >= 13 && hour <= 23) return `${hour - 12}pm`;
    return null;
  }
  const isPm = meridiem.startsWith("p");
  if (hour === 12) return isPm ? "Noon" : "Midnight";
  if (hour < 1 || hour > 11) return null;
  return `${hour}${isPm ? "pm" : "am"}`;
};

const normalizeScheduleDayOfWeek = (raw) => {
  if (raw == null || raw === "") return null;
  const s = String(raw).trim().toLowerCase();
  return SCHEDULE_DAYS_OF_WEEK.find((d) => d.toLowerCase() === s || d.toLowerCase().slice(0, 3) === s.slice(0, 3)) || null;
};

const normalizeScheduleDayOfMonth = (raw) => {
  if (raw == null || raw === "") return null;
  const m = String(raw).match(/(\d{1,2})/);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  return n >= 1 && n <= 31 ? String(n) : null;
};

const normalizeScheduleTriggerEvent = (raw) => {
  if (raw == null || raw === "") return null;
  const s = String(raw).trim();
  const exact = ["Every Month", "Every Week", "Every Day", "Every Hour", "Custom Interval"]
    .find((e) => e.toLowerCase() === s.toLowerCase());
  if (exact) return exact;
  const low = s.toLowerCase();
  if (low.includes("month")) return "Every Month";
  if (low.includes("week")) return "Every Week";
  if (low.includes("day")) return "Every Day";
  if (low.includes("hour")) return "Every Hour";
  if (low.includes("interval") || low.includes("minute") || low.includes("custom")) return "Custom Interval";
  return null;
};

const normalizeScheduleConditional = (conditional) => {
  if (!conditional || typeof conditional !== "object") return conditional;
  const aliases = {
    "Trigger Event": ["Trigger Event", "trigger_event", "triggerEvent", "event"],
    "Time of Day": ["Time of Day", "time_of_day", "timeOfDay", "time"],
    "Day of Week": ["Day of Week", "day_of_week", "dayOfWeek", "weekday"],
    "Day of Month": ["Day of Month", "day_of_month", "dayOfMonth", "day"],
    minutes: ["minutes", "Minutes", "interval_minutes", "intervalMinutes"],
  };
  const raw = {};
  Object.entries(aliases).forEach(([canon, keys]) => {
    for (const k of keys) {
      if (conditional[k] != null && conditional[k] !== "") {
        raw[canon] = conditional[k];
        break;
      }
    }
  });
  const event = normalizeScheduleTriggerEvent(raw["Trigger Event"]) || "Every Hour";
  const out = { "Trigger Event": event };
  if (event === "Every Month") {
    out["Day of Month"] = normalizeScheduleDayOfMonth(raw["Day of Month"]) || "1";
    out["Time of Day"] = normalizeScheduleTimeOfDay(raw["Time of Day"]) || "9am";
  } else if (event === "Every Week") {
    out["Day of Week"] = normalizeScheduleDayOfWeek(raw["Day of Week"]) || "Monday";
    out["Time of Day"] = normalizeScheduleTimeOfDay(raw["Time of Day"]) || "9am";
  } else if (event === "Every Day") {
    out["Time of Day"] = normalizeScheduleTimeOfDay(raw["Time of Day"]) || "9am";
  } else if (event === "Custom Interval") {
    const mins = raw.minutes == null || raw.minutes === "" ? "30" : String(raw.minutes).trim();
    out.minutes = mins || "30";
  }
  return out;
};

/**
 * Build createTrigger payload fields from trigger.fields.
 * Uses agent's trigger fields as-is (grounded in real schema from LLM/backend).
 * Only unwraps { value: x } wrappers; preserves object values (e.g. schedule conditional).
 * For schedule app, field "schedule" uses in: "formData" per real schema when missing.
 * Normalizes schedule conditional into the exact enum strings / required siblings the Lambda expects.
 */
const processTriggerFieldsForCreateTrigger = (fields, app) => {
  return (fields || []).map((f) => {
    let val = f?.value != null ? extractTriggerFieldValue(f.value) : f?.value;
    if (app === "schedule" && val != null && typeof val === "object") {
      const conditional =
        val.conditional != null && typeof val.conditional === "object"
          ? val.conditional
          : val["Trigger Event"] != null || val.trigger_event != null || val.triggerEvent != null
            ? val
            : null;
      if (conditional) {
        val = { conditional: normalizeScheduleConditional(conditional) };
      }
    }
    let inVal = f?.in;
    if (app === "schedule" && (f?.name === "schedule" || f?.name === "schedule_preset") && inVal == null)
      inVal = "formData";
    return {
      ...(f?.name != null && { name: f.name }),
      ...(inVal != null && { in: inVal }),
      ...(val !== undefined && val !== null && { value: val }),
    };
  });
};

/** Fetch schedule app trigger schema and return default fields so createTrigger gets a valid payload when agent has none. */
const fetchScheduleDefaultFields = async () => {
  const scheduleUrl = getBuiltInAppFormDataEndpoint("schedule");
  if (!scheduleUrl) return [];
  try {
    const res = await httpClient.get(scheduleUrl, { timeout: 8000 });
    const triggers = res.data?.triggers;
    if (!triggers || typeof triggers !== "object") return [];
    const firstTrigger = Object.values(triggers)[0];
    const fields = firstTrigger?.fields;
    if (!Array.isArray(fields) || fields.length === 0) return [];
    const first = fields[0];
    const name = first?.name || "schedule";
    const inVal = first?.in || "formData";
    const schema = first?.schema;
    const enum0 = schema?.definitions?.person?.properties?.["Trigger Event"]?.enum?.[0];
    const value = { conditional: { "Trigger Event": enum0 || "Every Hour" } };
    return [{ name, in: inVal, value }];
  } catch {
    return [];
  }
};

/** Build djangocaller createTrigger `data` payload from an agent + its primary trigger. */
const buildCreateTriggerDataForAgent = async (agent, trigger) => {
  const workflowId = agent?.workflow_id;
  if (!workflowId) throw new Error("Agent workflow_id is required to register a trigger.");
  const app = String(trigger.app || trigger.app_label || "").toLowerCase().trim();
  if (!app) throw new Error("Trigger app is required to register a trigger.");

  let fieldsForPayload = processTriggerFieldsForCreateTrigger(trigger.fields, app);
  if (app === "schedule" && fieldsForPayload.length === 0) {
    const defaultFields = await fetchScheduleDefaultFields();
    if (defaultFields.length > 0) fieldsForPayload = processTriggerFieldsForCreateTrigger(defaultFields, app);
  }

  const performList = resolveAgentTriggerPerformList(trigger);
  const polling = isAgentPollingTrigger(trigger);
  let triggerResource = "";
  if (polling) {
    triggerResource = performList || String(trigger.triggerResource || "").trim();
    if (!triggerResource) throw new Error("Polling trigger requires performList or triggerResource.");
  } else if (isBuiltInTriggerApp(app)) {
    // Schedule/forms/webhook: prefer the formData action (e.g. createSchedule), not the display key.
    triggerResource =
      String(trigger.trigger_action || trigger.triggerResource || "").trim() ||
      String(trigger.trigger_key || trigger.type || trigger.performList || "").trim();
  } else {
    triggerResource =
      trigger.triggerResource ||
      trigger.trigger_action ||
      performList ||
      trigger.trigger_key ||
      trigger.type ||
      "";
  }

  const url = trigger.url || getBuiltInAppFormDataEndpoint(app) || "";
  if (polling && !url) throw new Error("Polling trigger requires app url.");

  return {
    workflow_id: String(workflowId),
    start_node: trigger.trigger_id || "trigger-1",
    triggerApp: app,
    triggerResource,
    fields: fieldsForPayload,
    credential: trigger.credential || "",
    pollingParams:
      trigger.pollingParams && typeof trigger.pollingParams === "object"
        ? trigger.pollingParams
        : polling
          ? {}
          : {},
    url,
    ...(agent.agent_id ? { agent_id: agent.agent_id } : {}),
  };
};

/**
 * Register the agent's trigger with the backend (schedule, built-in webhook/forms, or polling).
 * Same contract as workflow TriggerPanel / AppBar createTrigger.
 * Polling createTrigger can take well over 15s (credential + list setup + schedule).
 */
const CREATE_TRIGGER_TIMEOUT_MS = 60000;

export const createTriggerForAgent = async (agent) => {
  const trigger = (agent?.triggers || [])[0];
  if (!trigger || !agentTriggerNeedsCreateTrigger(trigger)) return null;
  const data = await buildCreateTriggerDataForAgent(agent, trigger);
  const res = await djangocallerHttpClient.post(
    DJANGOCALLER_PATH,
    { operation: "createTrigger", data },
    { timeout: CREATE_TRIGGER_TIMEOUT_MS },
  );
  return res.data;
};

/** On start: createTrigger and merge triggerMessage onto the agent trigger. */
const registerAgentTriggerIfNeeded = async (agent) => {
  const trigger = (agent?.triggers || [])[0];
  if (!trigger || !agentTriggerNeedsCreateTrigger(trigger)) return agent;
  const triggerMessage = await createTriggerForAgent(agent);
  if (triggerMessage == null) return agent;
  return { ...agent, triggers: [{ ...trigger, triggerMessage }] };
};

/** On pause: stop polling schedule for polling triggers (mirrors workflow deleteTrigger). */
const stopAgentTriggerIfNeeded = async (agent) => {
  const trigger = (agent?.triggers || [])[0];
  if (!trigger || !isAgentPollingTrigger(trigger) || !agent?.workflow_id) return;
  await djangocallerHttpClient.post(
    DJANGOCALLER_PATH,
    {
      operation: "createSchedule",
      data: { webhookURL: String(agent.workflow_id), scheduleType: "stopPolling" },
    },
    { timeout: 15000 },
  );
};

/** Dedupe concurrent preview sample fetches (rapid Preview clicks / save+preview overlap). */
let sampleTriggerFetchInFlight = null;
const sampleTriggerFetchCache = { key: "", payload: null, at: 0 };
const SAMPLE_TRIGGER_CACHE_MS = 90_000;

const buildSampleTriggerFetchKey = (trigger, workflowId) => {
  const performList = resolveAgentTriggerPerformList(trigger);
  const fields = (trigger?.fields || [])
    .filter((f) => f?.name != null)
    .map((f) => ({
      name: f.name,
      in: f.in || "path",
      value: f?.value != null ? extractTriggerFieldValue(f.value) : f?.value,
    }));
  return JSON.stringify({
    app: trigger?.app || trigger?.app_label || "",
    url: trigger?.url || "",
    credential: trigger?.credential || "",
    performList,
    fields,
    workflowId: workflowId || "",
  });
};

const extractFirstSampleFromGetDDResponse = (obj) => {
  const extractArray = (o) => {
    if (Array.isArray(o)) return o;
    if (o && typeof o === "object") {
      for (const key of Object.keys(o)) {
        if (Array.isArray(o[key])) return o[key];
      }
    }
    return [];
  };
  const choices = extractArray(obj);
  const first = choices && choices[0];
  return first && typeof first === "object" ? first : null;
};

/**
 * @param {object} trigger - Agent trigger config
 * @param {{ workflowId?: string, agentId?: string, forceRefresh?: boolean }} [options]
 *   - workflowId: passed to createTrigger for built-in apps
 *   - forceRefresh: bypass test_data / in-memory cache (default false)
 */
export const fetchSampleTriggerDataForAgent = async (trigger, options = {}) => {
  if (!trigger) return null;
  const app = String(trigger.app || trigger.app_label || "").toLowerCase().trim();
  if (!app) return null;
  const url = trigger.url || "";
  const workflowId = options.workflowId != null && String(options.workflowId).trim() !== "" ? String(options.workflowId) : "agent-preview";
  const forceRefresh = options.forceRefresh === true;

  if (
    !forceRefresh &&
    trigger.test_data &&
    typeof trigger.test_data === "object" &&
    !Array.isArray(trigger.test_data)
  ) {
    return trigger.test_data;
  }

  const cacheKey = buildSampleTriggerFetchKey(trigger, workflowId);
  if (
    !forceRefresh &&
    sampleTriggerFetchCache.key === cacheKey &&
    sampleTriggerFetchCache.payload &&
    Date.now() - sampleTriggerFetchCache.at < SAMPLE_TRIGGER_CACHE_MS
  ) {
    return sampleTriggerFetchCache.payload;
  }

  if (sampleTriggerFetchInFlight && sampleTriggerFetchInFlight.key === cacheKey) {
    return sampleTriggerFetchInFlight.promise;
  }

  const run = async () => {
    try {
      // Workload Forms: createTrigger only registers the form — it is NOT a submission sample.
      // Preview waits for a real form submission (or uses trigger.test_data when already set).
      if (app === "workload-forms") {
        return null;
      }
      if (isBuiltInTriggerApp(app)) {
        const data = await buildCreateTriggerDataForAgent(
          { workflow_id: workflowId, agent_id: options.agentId, triggers: [trigger] },
          trigger,
        );
        const res = await djangocallerHttpClient.post(
          DJANGOCALLER_PATH,
          { operation: "createTrigger", data },
          { timeout: CREATE_TRIGGER_TIMEOUT_MS },
        );
        const responseData = res.data;
        if (app === "schedule" && responseData) {
          return typeof responseData === "object" ? responseData : { __raw: responseData };
        }
        if (app === "webhook" && responseData && typeof responseData === "object") {
          return responseData;
        }
        if (app === "schedule") return buildScheduleFallbackPayload(trigger);
        return null;
      }

      const performList = resolveAgentTriggerPerformList(trigger);
      if (!performList || !url) return null;

      const replacedParams = (trigger.fields || [])
        .filter((f) => f?.name != null)
        .map((f) => ({
          name: f.name,
          in: f.in || "path",
          value: f?.value != null ? extractTriggerFieldValue(f.value) : f?.value,
        }));

      const obj = await fetchAgentGetDDOptions({
        url,
        app: trigger.app,
        credential: trigger.credential || "",
        action: performList,
        fields: replacedParams,
      });
      return extractFirstSampleFromGetDDResponse(obj);
    } catch (err) {
      if (isBuiltInTriggerApp(app) && app === "schedule") {
        return buildScheduleFallbackPayload(trigger);
      }
      return null;
    }
  };

  const promise = run().then((payload) => {
    if (payload && typeof payload === "object") {
      sampleTriggerFetchCache.key = cacheKey;
      sampleTriggerFetchCache.payload = payload;
      sampleTriggerFetchCache.at = Date.now();
    }
    return payload;
  });

  sampleTriggerFetchInFlight = { key: cacheKey, promise };
  try {
    return await promise;
  } finally {
    if (sampleTriggerFetchInFlight?.key === cacheKey) {
      sampleTriggerFetchInFlight = null;
    }
  }
};

const AGENT_FORM_EDITOR_BASE = "https://editor.workload.co/form";

/** Public form URL for an agent's Workload Forms trigger (workflow id is the form id). */
export const getAgentFormPreviewUrl = (agent) => {
  const workflowId = String(agent?.workflow_id || "").trim();
  if (!workflowId) return null;
  // Same-origin in local/dev so form.jsx can bridge the sample via localStorage to Preview.
  try {
    if (typeof window !== "undefined" && window.location?.origin) {
      const host = String(window.location.hostname || "");
      if (host === "localhost" || host === "127.0.0.1") {
        return `${window.location.origin}/form/${workflowId}`;
      }
    }
  } catch (_) {
    /* fall through */
  }
  return `${AGENT_FORM_EDITOR_BASE}/${workflowId}`;
};

/** True when agent trigger is workload-forms. */
export const isAgentWorkloadFormsTrigger = (agentOrTrigger) => {
  const trigger = agentOrTrigger?.triggers ? (agentOrTrigger.triggers || [])[0] : agentOrTrigger;
  const app = String(trigger?.app || trigger?.app_label || "").toLowerCase().trim();
  return app === "workload-forms";
};

/** True when trigger.test_data looks like a real submission object (not empty / not array). */
export const hasUsableAgentFormSample = (trigger) => {
  const td = trigger?.test_data;
  return Boolean(td && typeof td === "object" && !Array.isArray(td) && Object.keys(td).length > 0);
};

/**
 * Preview needs an interactive form submission when trigger is workload-forms and
 * there is no cached test_data yet.
 */
export const agentNeedsFormSampleCapture = (agent) => {
  if (!isAgentWorkloadFormsTrigger(agent)) return false;
  const trigger = (agent?.triggers || [])[0];
  return !hasUsableAgentFormSample(trigger);
};

const normalizeFormsQuestionForRenderer = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const questionText =
    String(raw.question || "").trim() ||
    String(raw.label || "").trim() ||
    String(raw.title || "").trim() ||
    String(raw.id || "").trim() ||
    "Untitled question";
  const rawType = String(raw.type || "input").toLowerCase().trim();
  const typeMap = {
    input: "input",
    short_text: "input",
    text: "input",
    string: "input",
    email: "input",
    phone: "input",
    tel: "input",
    number: "input",
    date: "input",
    url: "input",
    textarea: "textarea",
    long_text: "textarea",
    paragraph: "textarea",
    checkboxes: "checkboxes",
    checkbox: "checkboxes",
    multi_select: "checkboxes",
    multiple_choice: "multiple_choice",
    radio: "multiple_choice",
    select: "multiple_choice",
    dropdown: "multiple_choice",
    yes_no: "multiple_choice",
    boolean: "multiple_choice",
    bool: "multiple_choice",
    file: "file",
    upload: "file",
  };
  let type = typeMap[rawType] || "input";
  const pickOpts = (...cands) => {
    for (const c of cands) {
      if (!Array.isArray(c)) continue;
      const opts = c
        .map((x) => (typeof x === "string" ? x : x?.label || x?.value || x?.name))
        .filter(Boolean)
        .map(String);
      if (opts.length) return opts;
    }
    return [];
  };
  const out = {
    type,
    question: questionText,
    required: raw.required === true || raw.required === "true" || raw.required === 1,
  };
  if (rawType === "yes_no" || rawType === "boolean" || rawType === "bool") {
    out.type = "multiple_choice";
    out.multiple_choice_options = pickOpts(raw.multiple_choice_options, raw.options, raw.choices);
    if (!out.multiple_choice_options.length) out.multiple_choice_options = ["Yes", "No"];
  } else if (out.type === "multiple_choice") {
    out.multiple_choice_options =
      pickOpts(raw.multiple_choice_options, raw.options, raw.choices) || ["Option 1", "Option 2"];
    if (!out.multiple_choice_options.length) out.multiple_choice_options = ["Option 1", "Option 2"];
  } else if (out.type === "checkboxes") {
    out.checkbox_options = pickOpts(raw.checkbox_options, raw.options, raw.choices);
    if (!out.checkbox_options.length) out.checkbox_options = ["Option 1", "Option 2"];
  }
  return out;
};

/** Coerce agent form trigger fields into the /form renderer contract. */
export const normalizeWorkloadFormsTriggerFields = (fields) => {
  const list = Array.isArray(fields) ? fields : [];
  const byName = {};
  list.forEach((f) => {
    const name = String(f?.name || f?.title || "")
      .trim()
      .toLowerCase();
    if (name) byName[name] = f;
  });
  const settingsVal = byName.settings?.value && typeof byName.settings.value === "object" ? byName.settings.value : {};
  const formMeta = settingsVal.form && typeof settingsVal.form === "object" ? settingsVal.form : {};
  const settingsOut = {
    form: {
      title: String(formMeta.title || "Untitled form"),
      description: String(formMeta.description || ""),
    },
    color: settingsVal.color && typeof settingsVal.color === "object" ? settingsVal.color : {},
    header: settingsVal.header && typeof settingsVal.header === "object" ? settingsVal.header : {},
    text_style:
      settingsVal.text_style && typeof settingsVal.text_style === "object"
        ? settingsVal.text_style
        : { text: {}, header: {}, question: {} },
  };
  const questionsOut = (Array.isArray(byName.questions?.value) ? byName.questions.value : [])
    .map(normalizeFormsQuestionForRenderer)
    .filter(Boolean);
  const sectionsOut = (Array.isArray(byName.additional_sections?.value) ? byName.additional_sections.value : [])
    .filter((s) => s && typeof s === "object")
    .map((sec) => ({
      section: (Array.isArray(sec.section) ? sec.section : [])
        .map(normalizeFormsQuestionForRenderer)
        .filter(Boolean),
    }));
  const toBool = (name, fallback) => {
    const v = byName[name]?.value;
    if (v === true || v === "true" || v === 1) return true;
    if (v === false || v === "false" || v === 0) return false;
    return fallback;
  };
  return [
    { name: "settings", in: "query", value: settingsOut },
    { name: "questions", in: "query", value: questionsOut },
    { name: "additional_sections", in: "query", value: sectionsOut },
    { name: "confirmation_page", in: "query", value: toBool("confirmation_page", true) },
    { name: "results_page", in: "query", value: toBool("results_page", false) },
  ];
};

/**
 * Ensure createTrigger has run so the form exists; merge triggerMessage onto the trigger.
 * Persists to the agent + syncs the agent workflow so /form/{workflow_id} can load webhook_url.
 * Returns updated agent (may be unchanged if registration fails or is unnecessary).
 */
export const ensureAgentFormTriggerRegistered = async (agent) => {
  if (!agent || !isAgentWorkloadFormsTrigger(agent)) return agent;
  const workflowId = String(agent.workflow_id || "").trim();
  if (!workflowId) throw new Error("Agent workflow_id is required to register the form.");
  const trigger = (agent.triggers || [])[0];
  if (!trigger) return agent;

  // Fix AI schema drift (label/short_text/yes_no → question/input/multiple_choice) before sync.
  const normalizedFields = normalizeWorkloadFormsTriggerFields(trigger.fields || []);
  let next = {
    ...agent,
    triggers: [{ ...trigger, fields: normalizedFields }],
  };
  const nextTrigger = next.triggers[0];

  const hasWebhook =
    nextTrigger.triggerMessage &&
    typeof nextTrigger.triggerMessage === "object" &&
    (nextTrigger.triggerMessage["Webhook URL"] ||
      nextTrigger.triggerMessage.webhookURL ||
      nextTrigger.triggerMessage.webhook_url);

  if (!hasWebhook) {
    try {
      const triggerMessage = await createTriggerForAgent(next);
      if (triggerMessage != null) {
        next = {
          ...next,
          triggers: [{ ...nextTrigger, triggerMessage }],
        };
      }
    } catch (err) {
      if (!nextTrigger.triggerMessage) throw err;
    }
  }

  const agentId = agent.agent_id;
  if (agentId) {
    try {
      const saved = await updateAgent(agentId, { triggers: next.triggers });
      if (saved && typeof saved === "object") next = { ...next, ...saved, triggers: next.triggers };
    } catch (_) {
      /* keep in-memory merge */
    }
  }

  // Sync triggerMessage + fields onto the django workflow so retrieveForm can serve the form.
  try {
    await updateWorkflowForAgentApi(workflowId, next, false);
  } catch (_) {
    /* form page may still work if createTrigger already wrote to the workflow */
  }
  return next;
};

/** localStorage key for same-origin form → Preview sample bridge (form.jsx writes; Preview polls). */
export const agentFormSampleStorageKey = (workflowId) =>
  `workload_agent_form_sample_${String(workflowId || "").trim()}`;

export const writeAgentFormSampleLocal = (workflowId, payload) => {
  const wf = String(workflowId || "").trim();
  if (!wf || payload == null || typeof payload !== "object" || typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(
      agentFormSampleStorageKey(wf),
      JSON.stringify({ payload, timestamp: Date.now() }),
    );
  } catch (_) {
    /* quota / private mode */
  }
};

export const readAgentFormSampleLocal = (workflowId, sinceTs = null) => {
  const wf = String(workflowId || "").trim();
  if (!wf || typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(agentFormSampleStorageKey(wf));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const payload = parsed?.payload;
    const ts = Number(parsed?.timestamp) || 0;
    if (payload == null || typeof payload !== "object") return null;
    if (sinceTs != null && ts < sinceTs) return null;
    return { id: `local-${wf}-${ts}`, payload, timestamp: ts, date: new Date(ts).toISOString() };
  } catch (_) {
    return null;
  }
};

const _payloadFromTriggerEvent = (te) => {
  if (te == null || typeof te !== "object") return null;
  if (te.payload != null && typeof te.payload === "object") return te.payload;
  if (te.data != null && typeof te.data === "object") return te.data;
  // Bare form answers object (no wrapper)
  if (!te.source && !te.type && Object.keys(te).length > 0) return te;
  return null;
};

/**
 * Fetch recent form submissions for an agent workflow.
 * Agent webhooks dispatch live runs (not always getExecutions), so we merge:
 * localStorage bridge, agent.last_webhook_payload, agent runs' trigger_event, then getExecutions.
 * @param {string} workflowId
 * @param {{ sinceTs?: number, triggerId?: string, agentId?: string }} [options]
 * @returns {Promise<Array<{ id: string, payload: object, timestamp: number, date?: string }>>}
 */
export const fetchAgentFormSubmissions = async (workflowId, options = {}) => {
  const wf = String(workflowId || "").trim();
  if (!wf) return [];
  // Small skew buffer so listen-start vs form-tab clocks don't miss a fresh sample.
  const sinceTs =
    typeof options.sinceTs === "number" ? Math.max(0, options.sinceTs - 5000) : null;
  const triggerId = options.triggerId != null ? String(options.triggerId).trim() : "";
  const agentId = options.agentId != null ? String(options.agentId).trim() : "";

  const submissions = [];
  const seen = new Set();
  const pushSub = (entry) => {
    if (!entry?.payload || typeof entry.payload !== "object") return;
    const keys = Object.keys(entry.payload);
    if (keys.length === 0) return;
    const id = String(entry.id || `${entry.timestamp}-${keys[0]}`);
    if (seen.has(id)) return;
    seen.add(id);
    submissions.push({
      id,
      payload: entry.payload,
      date: entry.date,
      timestamp: Number(entry.timestamp) || 0,
    });
  };

  const local = readAgentFormSampleLocal(wf, sinceTs);
  if (local) pushSub(local);

  if (agentId) {
    try {
      const agentRes = await httpClient.get(`/agents/${agentId}`, { timeout: 15000 });
      const agentDoc = agentRes?.data?.agent || agentRes?.data?.data || agentRes?.data;
      const payload = agentDoc?.last_webhook_payload;
      const at = agentDoc?.last_webhook_at;
      const ts = at ? new Date(at).getTime() : 0;
      if (payload && typeof payload === "object") {
        // When listening (sinceTs set), require last_webhook_at so we don't reuse a stale sample.
        if (sinceTs == null || (ts && ts >= sinceTs)) {
          pushSub({
            id: `agent-last-webhook-${agentId}-${ts || "na"}`,
            payload,
            timestamp: ts || Date.now(),
            date: at || undefined,
          });
        }
      }
    } catch (_) {
      /* optional */
    }

    try {
      const runsRes = await httpClient.get(`/agents/${agentId}/runs`, {
        params: { limit: 15, skip: 0 },
        timeout: 15000,
      });
      const page = normalizeRunListPageResponse(runsRes?.data);
      for (const run of page.runs || []) {
        const te = run?.trigger_event || run?.triggerEvent;
        const payload = _payloadFromTriggerEvent(te);
        if (!payload) continue;
        const ts =
          parseRunCreatedAtMs(run?.created_at) ||
          parseRunCreatedAtMs(run?.updated_at) ||
          0;
        if (sinceTs != null && ts && ts < sinceTs) continue;
        if (sinceTs != null && !ts) continue;
        pushSub({
          id: String(run.run_id || run.task_id || run._id || `run-${ts}`),
          payload,
          timestamp: ts || Date.now(),
          date: run.created_at || run.updated_at,
        });
      }
    } catch (_) {
      /* optional */
    }
  }

  try {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const timestamp = thirtyDaysAgo.toISOString();
    const res = await djangocallerHttpClient.post(
      DJANGOCALLER_PATH,
      {
        operation: "getExecutions",
        data: { workflow_id: wf, timestamp },
      },
      { timeout: 30000 },
    );
    const raw = res?.data;
    const data = Array.isArray(raw)
      ? raw
      : Array.isArray(raw?.data)
        ? raw.data
        : Array.isArray(raw?.executions)
          ? raw.executions
          : [];
    if (Array.isArray(data)) {
      for (const exec of data) {
        const nodes = exec?.state?.nodes;
        if (!nodes || typeof nodes !== "object") continue;
        let nodeKey = null;
        if (triggerId && nodes[triggerId]?.outputFields != null) {
          nodeKey = triggerId;
        } else {
          nodeKey =
            Object.keys(nodes).find((id) => nodes[id]?.outputFields != null) || null;
        }
        if (!nodeKey) continue;
        const payload = nodes[nodeKey].outputFields;
        if (payload == null || typeof payload !== "object") continue;
        const ts =
          exec.timestamp ||
          (exec.date_run || exec.created_at
            ? new Date(exec.date_run || exec.created_at).getTime()
            : 0) ||
          0;
        if (sinceTs != null && ts < sinceTs) continue;
        pushSub({
          id: String(exec.id || exec._id || `exec-${ts}`),
          payload,
          date: exec.date_run || exec.created_at,
          timestamp: ts,
        });
      }
    }
  } catch (_) {
    /* keep other sources */
  }

  submissions.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
  return submissions.slice(0, 10);
};

/** Activate the agent scaffold workflow so form submissions are recorded (preview listening). */
export const activateAgentWorkflowForListening = async (agent) => {
  const workflowId = String(agent?.workflow_id || "").trim();
  if (!workflowId) throw new Error("Agent workflow_id is required to listen for form submissions.");
  await updateWorkflowForAgentApi(workflowId, agent, true);
  return workflowId;
};

/**
 * Deactivate scaffold after listening, unless the agent is already running live.
 * @returns {Promise<boolean>} true if deactivated
 */
export const deactivateAgentWorkflowAfterListening = async (agent) => {
  const workflowId = String(agent?.workflow_id || "").trim();
  if (!workflowId) return false;
  const status = String(agent?.status || "").toLowerCase();
  if (status === "running") return false;
  await updateWorkflowForAgentApi(workflowId, agent, false);
  return true;
};

/**
 * Persist a form submission onto the agent's primary trigger as test_data.
 * Returns the updated agent from the API (or local merge if update fails shape).
 */
export const persistAgentFormSample = async (agentId, agent, samplePayload) => {
  if (!agentId || !samplePayload || typeof samplePayload !== "object") {
    throw new Error("Form sample payload is required.");
  }
  const trigger = (agent?.triggers || [])[0];
  if (!trigger) throw new Error("Agent has no trigger to attach form sample to.");
  const nextTriggers = [{ ...trigger, test_data: samplePayload }];
  const updated = await updateAgent(agentId, { triggers: nextTriggers });
  if (updated && typeof updated === "object") return updated;
  return { ...agent, triggers: nextTriggers };
};

/** Fetch credentials from docdb (tenant + user, label + slug). */
const fetchCredentialsForApp = async (tenant, user, appSlug, appLabel) =>
  fetchAllCredentialsForApp({ tenant, user, appSlug, appLabel });

/**
 * Enrich agent actions with user's existing credentials (lastUsed or latest) per app.
 * Call after applyDraftToAgent so new agents / new actions get credential and authname.
 */
export const enrichAgentActionsWithCredentials = async (agent) => {
  const actions = agent?.actions || [];
  if (actions.length === 0) return agent;
  const tenant = (typeof localStorage !== "undefined" && localStorage.getItem("tenant")) || "";
  const user = (typeof localStorage !== "undefined" && localStorage.getItem("user")) || "";
  const hasContext = (v) => v && String(v) !== "null" && String(v) !== "undefined";
  if (!hasContext(tenant) && !hasContext(user)) return agent;
  const appKeys = [...new Set(actions.map((a) => (a?.app || a?.app_label || "").toString().toLowerCase().trim()).filter(Boolean))];
  const credentialByApp = {};
  await Promise.all(
    appKeys.map(async (appKey) => {
      const firstAction = actions.find((a) => (a?.app || a?.app_label || "").toString().toLowerCase().trim() === appKey);
      const appLabel = firstAction?.app_label || firstAction?.app || appKey;
      const list = await fetchCredentialsForApp(tenant, user, appKey, appLabel);
      const picked = pickBestCredential(list);
      if (picked) {
        const id = picked._id?.$oid ?? picked._id;
        const idx = list.findIndex((c) => (c._id?.$oid ?? c._id) === id);
        const displayName = `${picked.app || appKey} - ${picked.name ?? "unnamed"}${idx >= 0 ? `#${idx + 1}` : ""}`;
        credentialByApp[appKey] = { credential: id, authname: displayName };
      }
    })
  );
  const nextActions = actions.map((action) => {
    const appKey = (action?.app || action?.app_label || "").toString().toLowerCase().trim();
    const existingCred = action.credential ?? action.config?.credential ?? "";
    if (existingCred) return action;
    const cred = appKey ? credentialByApp[appKey] : null;
    if (!cred) return action;
    return {
      ...action,
      credential: cred.credential,
      authname: cred.authname,
      config: {
        ...(action.config && typeof action.config === "object" ? action.config : {}),
        credential: cred.credential,
        authname: cred.authname,
      },
    };
  });
  return { ...agent, actions: nextActions };
};

/**
 * Enrich agent triggers with user's existing credentials (lastUsed or latest) per app.
 * Mirrors enrichAgentActionsWithCredentials — fixes "no auth connected" after copilot apply.
 */
export const enrichAgentTriggersWithCredentials = async (agent) => {
  const triggers = agent?.triggers || [];
  if (triggers.length === 0) return agent;
  const tenant = (typeof localStorage !== "undefined" && localStorage.getItem("tenant")) || "";
  const user = (typeof localStorage !== "undefined" && localStorage.getItem("user")) || "";
  const hasContext = (v) => v && String(v) !== "null" && String(v) !== "undefined";
  if (!hasContext(tenant) && !hasContext(user)) return agent;

  const BUILTIN_NO_AUTH = new Set(["schedule", "webhook", "workload-forms"]);
  const appKeys = [
    ...new Set(
      triggers
        .map((t) => (t?.app || t?.app_label || "").toString().toLowerCase().trim())
        .filter((k) => k && !BUILTIN_NO_AUTH.has(k))
    ),
  ];
  const credentialByApp = {};
  await Promise.all(
    appKeys.map(async (appKey) => {
      const firstTrigger = triggers.find(
        (t) => (t?.app || t?.app_label || "").toString().toLowerCase().trim() === appKey
      );
      const appLabel = firstTrigger?.app_label || firstTrigger?.app || appKey;
      const list = await fetchCredentialsForApp(tenant, user, appKey, appLabel);
      const picked = pickBestCredential(list);
      if (picked) {
        const id = picked._id?.$oid ?? picked._id;
        const idx = list.findIndex((c) => (c._id?.$oid ?? c._id) === id);
        const displayName = `${picked.app || appKey} - ${picked.name ?? "unnamed"}${idx >= 0 ? `#${idx + 1}` : ""}`;
        credentialByApp[appKey] = { credential: id, authname: displayName };
      }
    })
  );

  const nextTriggers = triggers.map((trigger) => {
    const appKey = (trigger?.app || trigger?.app_label || "").toString().toLowerCase().trim();
    if (!appKey || BUILTIN_NO_AUTH.has(appKey)) return trigger;
    const existingCred = trigger.credential ?? "";
    if (existingCred) return trigger;
    const cred = credentialByApp[appKey];
    if (!cred) return trigger;
    return {
      ...trigger,
      credential: cred.credential,
      authname: cred.authname,
    };
  });
  return { ...agent, triggers: nextTriggers };
};

/** Auto-select credentials for both triggers and actions after copilot draft apply. */
export const enrichAgentWithCredentials = async (agent) => {
  let next = await enrichAgentActionsWithCredentials(agent);
  next = await enrichAgentTriggersWithCredentials(next);
  return next;
};

const FILE_DATA_KEYS_FOR_BLOB = new Set([
  "file", "data", "content", "base64", "body", "attachment",
  "filecontent", "file_content", "filedata", "file_data", "bytes", "binary",
  "payload", "document", "filebody", "file_body", "raw", "buffer",
]);

/** Collect base64 file fields from executeAction output before enrich strips them. */
export function extractFileBlobsFromOutput(output, into = {}) {
  if (output == null) return into;
  if (Array.isArray(output)) {
    output.forEach((item) => extractFileBlobsFromOutput(item, into));
    return into;
  }
  if (typeof output !== "object") return into;
  const existing = output._file_blobs;
  if (existing && typeof existing === "object" && !Array.isArray(existing)) {
    Object.entries(existing).forEach(([k, v]) => {
      if (typeof k === "string" && typeof v === "string" && v.replace(/\s+/g, "").length >= 64) {
        into[k] = v.replace(/\s+/g, "");
      }
    });
  }
  for (const [key, val] of Object.entries(output)) {
    if (key === "_file_blobs") continue;
    if (typeof val === "string" && FILE_DATA_KEYS_FOR_BLOB.has(key.toLowerCase())) {
      const compact = val.replace(/\s+/g, "");
      const dataUrl = /^data:[^;]+;base64,(.+)$/i.exec(val.trim());
      const b64 = dataUrl ? dataUrl[1].replace(/\s+/g, "") : compact;
      if (b64.length >= 64 && /^[A-Za-z0-9+/=]+$/.test(b64.slice(0, 8192))) {
        into[key] = b64;
      }
    } else if (val && typeof val === "object") {
      extractFileBlobsFromOutput(val, into);
    }
  }
  return into;
}

/** Reattach stashed blobs + upload_ref after enrichToolOutput for later Google Drive / file uploads. */
export function attachFileBlobsForUpload(enriched, blobs, stepIndex = null) {
  if (!enriched || typeof enriched !== "object" || Array.isArray(enriched)) return enriched;
  if (!blobs || !Object.keys(blobs).length) return enriched;
  const out = { ...enriched, _file_blobs: { ...(enriched._file_blobs || {}), ...blobs } };
  const fe = out.file_extraction && typeof out.file_extraction === "object"
    ? { ...out.file_extraction }
    : {};
  let src = String(fe.source_key || "").trim();
  if (!src) src = Object.keys(blobs)[0] || "attachment";
  fe.source_key = src;
  if (stepIndex != null && Number.isFinite(Number(stepIndex))) {
    const i = Number(stepIndex);
    fe.step_index = i;
    fe.upload_ref = `prior_file:step${i}:${src}`;
  } else {
    fe.upload_ref = `prior_file:${src}`;
  }
  fe.file_available_for_upload = true;
  out.file_extraction = fe;
  if (out._file_blobs && src && out._file_blobs[src] && fe.upload_ref) {
    out._file_blobs = {
      ...out._file_blobs,
      [fe.upload_ref]: out._file_blobs[src],
      [`step${fe.step_index}:${src}`]: out._file_blobs[src],
    };
  }
  return out;
}

/** Assign unique prior_file:stepN:source_key refs so multi-file uploads don't collide. */
export function assignStepUploadRefs(priorSteps) {
  if (!Array.isArray(priorSteps)) return priorSteps;
  return priorSteps.map((step, i) => {
    if (!step || typeof step !== "object") return step;
    let output = step.output;
    if (typeof output === "string") {
      try {
        const parsed = JSON.parse(output);
        if (parsed && typeof parsed === "object") output = parsed;
        else return step;
      } catch {
        return step;
      }
    }
    if (!output || typeof output !== "object" || Array.isArray(output)) return step;
    const blobs = extractFileBlobsFromOutput(output);
    if (!Object.keys(blobs).length && !output.file_extraction) return step;
    const annotated = attachFileBlobsForUpload(output, blobs, i);
    return { ...step, output: annotated };
  });
}

function stripFileBlobsFromOutputForBrain(output) {
  if (output == null) return output;
  if (typeof output === "string") {
    const s = output.trim();
    // Hard gate: never send multi-KB base64-ish strings to the brain over WS.
    if (s.length > 2000) {
      const sample = s.slice(0, 4000).replace(/\s/g, "");
      const base64ish = /^[A-Za-z0-9+/=]+$/.test(sample) && !s.startsWith("{") && !s.startsWith("[") && !/^https?:/i.test(s);
      if (base64ish || /^data:[^;]+;base64,/i.test(s)) {
        return `[binary omitted — ${s.length} chars]`;
      }
    }
    return output;
  }
  if (Array.isArray(output)) return output.map(stripFileBlobsFromOutputForBrain);
  if (typeof output !== "object") return output;
  const out = {};
  for (const [k, v] of Object.entries(output)) {
    if (k === "_file_blobs") continue;
    out[k] = stripFileBlobsFromOutputForBrain(v);
  }
  return out;
}

/**
 * A step can succeed at running and still say dependent work must not proceed
 * (e.g. {"action":"skip","reason":"Invoice balance is 0"}). Matched on returned
 * *values*, never field names — mirrors backend detect_control_directive.
 */
const CONTROL_HALT_TOKENS = new Set([
  "skip", "stop", "halt", "abort", "cancel", "exit", "bail",
  "terminate", "noop", "no_op", "none", "end",
]);
const CONTROL_REASON_KEYS = ["reason", "message", "detail", "details", "why", "note"];

function detectControlDirective(output) {
  if (!output || typeof output !== "object" || Array.isArray(output)) return null;
  for (const ck of ["control", "_control"]) {
    const c = output[ck];
    if (c && typeof c === "object" && !Array.isArray(c)) {
      const d = String(c.directive || c.action || "").trim().toLowerCase();
      if (d) {
        return {
          directive: d,
          field: ck,
          reason: String(c.reason || "").slice(0, 240),
          halts: CONTROL_HALT_TOKENS.has(d),
        };
      }
    }
  }
  const keys = Object.keys(output);
  // A directive is a decision, not a dataset.
  if (keys.length === 0 || keys.length > 8) return null;
  const reason = (() => {
    for (const rk of CONTROL_REASON_KEYS) {
      const v = output[rk];
      if (typeof v === "string" && v.trim()) return v.trim().slice(0, 240);
    }
    return "";
  })();
  for (const [k, v] of Object.entries(output)) {
    if (typeof v === "string" && CONTROL_HALT_TOKENS.has(v.trim().toLowerCase())) {
      return { directive: v.trim().toLowerCase(), field: k, reason, halts: true };
    }
  }
  for (const [k, v] of Object.entries(output)) {
    if (v === true && CONTROL_HALT_TOKENS.has(String(k).trim().toLowerCase())) {
      return { directive: String(k).trim().toLowerCase(), field: k, reason, halts: true };
    }
  }
  return null;
}

// API Gateway WebSocket frames die at 128KB. One JPEG base64 (~230KB) in a
// receipt closes the socket before the brain can propose the next step.
const PREVIEW_WS_FRAME_BUDGET = 110000;
const PREVIEW_STRUCT_SKIP = new Set([
  "catalog",
  "primaryresource",
  "fileextraction",
  "uploadhint",
  "workloadresponse",
  "fileblobs",
]);
const PREVIEW_BINARY_KEYS = new Set(["file", "bytes", "content", "base64", "dataurl", "rawbody"]);

function normPreviewStructKey(key) {
  return String(key || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function looksLikePreviewBase64Blob(value) {
  if (typeof value !== "string") return false;
  const s = value.trim();
  if (s.length < 2000) return false;
  if (/^data:[^;]+;base64,/i.test(s)) return true;
  const sample = s.slice(0, 4000).replace(/\s/g, "");
  if (!sample || sample.startsWith("{") || sample.startsWith("[") || sample.startsWith("<")) return false;
  if (/^https?:/i.test(sample)) return false;
  return /^[A-Za-z0-9+/=]+$/.test(sample);
}

/** Depth-capped copy for receipts. Drops `_file_blobs` and long base64 strings. */
function projectStructureForReceipt(output, depth = 0) {
  if (output == null || depth > 4) return null;
  if (typeof output === "boolean" || typeof output === "number") return output;
  if (typeof output === "string") {
    const s = output.trim();
    if (!s || looksLikePreviewBase64Blob(s)) return null;
    return s.length > 200 ? `${s.slice(0, 200)}…` : s;
  }
  if (Array.isArray(output)) {
    const out = [];
    for (const item of output.slice(0, 5)) {
      const proj = projectStructureForReceipt(item, depth + 1);
      if (proj == null || proj === "") continue;
      if (Array.isArray(proj) && proj.length === 0) continue;
      if (typeof proj === "object" && !Array.isArray(proj) && !Object.keys(proj).length) continue;
      out.push(proj);
    }
    return out.length ? out : null;
  }
  if (typeof output !== "object") return null;
  const projected = {};
  let i = 0;
  for (const [k, v] of Object.entries(output)) {
    if (i >= 48) break;
    i += 1;
    if (typeof k !== "string") continue;
    const kn = normPreviewStructKey(k);
    if (!kn || PREVIEW_STRUCT_SKIP.has(kn)) continue;
    if (PREVIEW_BINARY_KEYS.has(kn) && typeof v === "string" && v.length > 200) continue;
    if (v != null && typeof v === "object") {
      const nested = projectStructureForReceipt(v, depth + 1);
      if (nested == null) continue;
      if (Array.isArray(nested) && nested.length === 0) continue;
      if (typeof nested === "object" && !Array.isArray(nested) && !Object.keys(nested).length) continue;
      projected[k] = nested;
    } else if (typeof v === "string") {
      const s = v.trim();
      if (s && s.length <= 200 && !looksLikePreviewBase64Blob(s)) projected[k] = s;
    } else if (v == null || typeof v === "boolean" || typeof v === "number") {
      projected[k] = v;
    }
  }
  return Object.keys(projected).length ? projected : null;
}

function messageUtf8Bytes(msg) {
  const json = JSON.stringify(msg);
  if (typeof TextEncoder !== "undefined") {
    try {
      return new TextEncoder().encode(json).length;
    } catch (_) {
      /* fall through */
    }
  }
  return json.length;
}

/** Compact key=value summary for small structured results (never collapse to bare "OK"). */
function summarizeStructuredOutput(output) {
  if (!output || typeof output !== "object" || Array.isArray(output)) return null;
  const parts = [];
  for (const [k, v] of Object.entries(output)) {
    if (v == null || typeof v === "object") continue;
    parts.push(`${k}=${String(v).slice(0, 80)}`);
    if (parts.length >= 6) break;
  }
  return parts.length ? parts.join("; ") : null;
}

/** Build a brain-safe prior step from full output (receipt-first). */
function receiptFromPriorStep(step, stepIndex = 0) {
  if (!step || typeof step !== "object") return step;
  if (step.receipt && typeof step.receipt === "object") {
    const r = { ...step.receipt };
    if (r.structure != null) r.structure = projectStructureForReceipt(r.structure);
    delete r._file_blobs;
    const file = r.file && typeof r.file === "object" ? r.file : null;
    const brainOutput = {
      receipt: true,
      summary: r.summary,
      ok: r.ok,
      control: r.control || null,
      status_code: r.status_code,
      resource_type: r.resource_type,
      ids: r.ids || [],
      count: r.count,
      primary_id: r.primary_id,
      error: r.error,
    };
    if (r.structure && typeof r.structure === "object") {
      brainOutput.structure = r.structure;
    }
    if (file?.upload_ref) {
      brainOutput.file_extraction = {
        upload_ref: file.upload_ref,
        mime_type: file.mime_type,
        extraction_method: file.extraction_method,
        file_available_for_upload: file.file_available_for_upload,
        extracted_text: file.extracted_text_preview || "",
      };
      brainOutput.upload_hint = {
        upload_ref: file.upload_ref,
        file_available_for_upload: file.file_available_for_upload,
      };
    }
    return {
      action_id: step.action_id,
      label: step.label,
      output: stripFileBlobsFromOutputForBrain(brainOutput),
      receipt: r,
    };
  }
  // Derive a minimal receipt from output when server didn't attach one yet
  let output = step.output;
  if (typeof output === "string") {
    try {
      const parsed = JSON.parse(output);
      if (parsed && typeof parsed === "object") output = parsed;
    } catch {
      /* keep */
    }
  }
  const fe =
    output && typeof output === "object" && output.file_extraction && typeof output.file_extraction === "object"
      ? output.file_extraction
      : null;
  const ids = [];
  if (output && typeof output === "object" && Array.isArray(output.data)) {
    output.data.forEach((row) => {
      if (row && typeof row === "object" && row.id != null) ids.push(String(row.id));
    });
  }
  const control = detectControlDirective(output);
  const structured = summarizeStructuredOutput(output);
  let summary =
    (fe && fe.upload_ref && `File ready; upload_ref=${fe.upload_ref}`) ||
    (ids.length ? `list: ${ids.length} id(s)` : null) ||
    (output && typeof output === "object" && output.error ? `Error: ${String(output.error).slice(0, 120)}` : null) ||
    structured ||
    "OK";
  // A halt directive must lead the summary — bare "OK" made the brain treat
  // {"action":"skip","reason":"Invoice balance is 0"} as permission to send SMS.
  if (control?.halts) {
    let lead = `DIRECTIVE ${String(control.directive).toUpperCase()}`;
    if (control.reason) lead += ` — ${control.reason}`;
    lead += ". Dependent work must not proceed.";
    summary = summary && summary !== "OK" ? `${lead} (${summary})` : lead;
  }
  const receipt = {
    step_index: stepIndex,
    action_id: step.action_id,
    label: step.label,
    ok: !(output && typeof output === "object" && (output.error || (output.status_code || 0) >= 400)),
    summary: String(summary).slice(0, 280),
    control: control || null,
    // Never attach the live output: it carries `_file_blobs` (full JPEG base64).
    structure:
      output && typeof output === "object" && !Array.isArray(output) && Object.keys(output).length <= 12
        ? projectStructureForReceipt(output)
        : null,
    ids: ids.slice(0, 40),
    count: ids.length || undefined,
    primary_id: (output && typeof output === "object" && output.id != null ? String(output.id) : null) || (ids.length === 1 ? ids[0] : null),
    file: fe
      ? {
          upload_ref: fe.upload_ref,
          mime_type: fe.mime_type || fe.mimeType,
          extraction_method: fe.extraction_method,
          file_available_for_upload: fe.file_available_for_upload,
          extracted_text_preview: String(fe.extracted_text || "").slice(0, 800),
        }
      : null,
  };
  return receiptFromPriorStep({ ...step, receipt }, stepIndex);
}

function listPriorFileEntries(priorSteps) {
  const steps = assignStepUploadRefs(priorSteps);
  const entries = [];
  if (!Array.isArray(steps)) return entries;
  steps.forEach((step, i) => {
    if (!step || typeof step !== "object") return;
    let output = step.output;
    if (typeof output === "string") {
      try {
        const parsed = JSON.parse(output);
        if (parsed && typeof parsed === "object") output = parsed;
        else return;
      } catch {
        return;
      }
    }
    if (!output || typeof output !== "object") return;
    const collected = extractFileBlobsFromOutput(output);
    if (!Object.keys(collected).length) return;
    const fe = output.file_extraction && typeof output.file_extraction === "object"
      ? output.file_extraction
      : {};
    let src = String(fe.source_key || "").trim();
    if (!src || !collected[src]) {
      src = Object.keys(collected).find((k) => !k.startsWith("prior_file:") && !/^step\d+:/.test(k))
        || Object.keys(collected)[0];
    }
    const b64 = collected[src] || Object.values(collected)[0];
    const ref = String(fe.upload_ref || "").trim() || `prior_file:step${i}:${src}`;
    const aid = String(step.action_id || "").trim();
    const aliases = new Set([
      src,
      `prior_file:${src}`,
      ref,
      `step${i}:${src}`,
      `prior_file:step${i}:${src}`,
    ]);
    if (aid) {
      aliases.add(`${aid}:${src}`);
      aliases.add(`prior_file:${aid}:${src}`);
    }
    entries.push({ stepIndex: i, actionId: aid, sourceKey: src, uploadRef: ref, b64, aliases });
  });
  return entries;
}

/** Shared resolver so a batch of N uploads each gets a distinct prior download. */
export function createPriorFileBlobResolver(priorSteps) {
  const entries = listPriorFileEntries(priorSteps);
  const byAlias = new Map();
  entries.forEach((entry) => {
    entry.aliases.forEach((alias) => {
      if (!byAlias.has(alias)) byAlias.set(alias, []);
      byAlias.get(alias).push(entry);
    });
  });
  const consumed = new Set();

  const take = (entry) => {
    consumed.add(entry.stepIndex);
    return entry.b64;
  };

  const nextUnused = (sourceKey = null) => {
    if (sourceKey) {
      for (const entry of entries) {
        if (consumed.has(entry.stepIndex)) continue;
        if (entry.sourceKey === sourceKey) return entry;
      }
    }
    for (const entry of entries) {
      if (!consumed.has(entry.stepIndex)) return entry;
    }
    return null;
  };

  const resolve = (val) => {
    const s = String(val || "").trim();
    if (!s || !entries.length) return null;
    const candidates = [];
    if (byAlias.has(s)) candidates.push(s);
    const m = /^prior_file:(.+)$/i.exec(s);
    if (m) {
      candidates.push(m[1].trim());
      candidates.push(`prior_file:${m[1].trim()}`);
      candidates.push(s);
    }
    const embedded = s.match(/prior_file:[\w.\-:]+/i);
    if (embedded) candidates.push(embedded[0]);

    for (const token of candidates) {
      const rows = byAlias.get(token) || [];
      for (const entry of rows) {
        if (!consumed.has(entry.stepIndex)) return take(entry);
      }
      if (/^prior_file:step\d+:/i.test(token) || /^step\d+:/.test(token)) {
        if (rows.length) return rows[0].b64;
      }
    }

    if (
      /\[omitted/i.test(s) ||
      /\[binary omitted/i.test(s) ||
      /see file_extraction/i.test(s) ||
      ["attachment", "file", "content", "data", "base64"].includes(s)
    ) {
      const entry = nextUnused();
      if (entry) return take(entry);
    }
    return null;
  };

  return { entries, resolve };
}

function fieldNameLooksLikeFile(name) {
  const n = String(name || "").trim().toLowerCase();
  if (!n) return false;
  if (FILE_DATA_KEYS_FOR_BLOB.has(n)) return true;
  const leaf = n.split(".").pop();
  if (FILE_DATA_KEYS_FOR_BLOB.has(leaf)) return true;
  return /file|attachment|document|content|binary|upload|media|photo|image|pdf/i.test(n);
}

/** Inject real base64 into proposed upload fields that reference a prior download. */
export function hydrateProposedFieldsWithPriorFiles(fields, priorSteps, resolver = null) {
  if (!Array.isArray(fields) || !fields.length) return fields;
  const active = resolver || createPriorFileBlobResolver(priorSteps);
  if (!active.entries.length) return fields;

  const hydrateValue = (val, force = false) => {
    if (typeof val === "string") {
      if (force || /^prior_file:/i.test(val.trim()) || /prior_file:/i.test(val)) {
        const resolved = active.resolve(val);
        return resolved != null ? resolved : val;
      }
      if (force && (/\[omitted/i.test(val) || /see file_extraction/i.test(val))) {
        const resolved = active.resolve(val);
        return resolved != null ? resolved : val;
      }
      return val;
    }
    if (Array.isArray(val)) return val.map((v) => hydrateValue(v, force));
    if (val && typeof val === "object") {
      const out = {};
      for (const [k, v] of Object.entries(val)) {
        out[k] = hydrateValue(v, force || fieldNameLooksLikeFile(k));
      }
      return out;
    }
    return val;
  };

  return fields.map((f) => {
    if (!f || typeof f !== "object") return f;
    const name = f.name || f.title;
    return {
      ...f,
      value: hydrateValue(f.value, fieldNameLooksLikeFile(name)),
    };
  });
}

/** prior_file:* values still present after hydrate — must not be posted as base64. */
export function unresolvedPriorFileRefs(fields) {
  const found = [];
  const walk = (val, force = false) => {
    if (typeof val === "string") {
      const s = val.trim();
      if (!s) return;
      if (/^prior_file:/i.test(s) || (force && /\[omitted|see file_extraction/i.test(s) && s.length < 500)) {
        found.push(s.slice(0, 120));
      }
      return;
    }
    if (Array.isArray(val)) {
      val.forEach((v) => walk(v, force));
      return;
    }
    if (val && typeof val === "object") {
      Object.entries(val).forEach(([k, v]) => walk(v, force || fieldNameLooksLikeFile(k)));
    }
  };
  if (!Array.isArray(fields)) return found;
  fields.forEach((f) => {
    if (!f || typeof f !== "object") return;
    walk(f.value, fieldNameLooksLikeFile(f.name || f.title));
  });
  return found;
}

/** Mirror backend prepare_tool_output_for_brain for trigger payloads sent to the brain. */
const prepareTriggerPayloadForBrain = (payload) => {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) return payload;
  const out = { ...payload };
  const fe = out.file_extraction;
  if (fe && typeof fe === "object") {
    const extracted = String(fe.extracted_text || "").trim();
    if (extracted) {
      out.document_text = extracted;
      if (typeof out.attachment === "string" && out.attachment.startsWith("http") && out.attachment.length > 160) {
        out.attachment = `${out.attachment.slice(0, 120)}… [see file_extraction.extracted_text]`;
      }
    }
  }
  return out;
};

/** Same trigger object is re-sent on every preview step. OCR it once. */
const enrichedTriggerCache = new WeakMap();

/** If trigger payload may contain file blobs/URLs, OCR/extract before the brain sees it. */
export async function enrichTriggerEventForBrain(triggerEvent) {
  if (!triggerEvent || typeof triggerEvent !== "object") return triggerEvent;
  if (enrichedTriggerCache.has(triggerEvent)) return enrichedTriggerCache.get(triggerEvent);
  const payload = triggerEvent.payload ?? triggerEvent;
  if (payload == null || typeof payload !== "object" || Array.isArray(payload)) return triggerEvent;
  try {
    const enrichedPayload = prepareTriggerPayloadForBrain(await enrichToolOutputForBrain(payload));
    const result = triggerEvent.payload != null
      ? { ...triggerEvent, payload: enrichedPayload }
      : enrichedPayload;
    const check = result?.payload ?? result;
    if (!outputNeedsFileEnrichment(check)) enrichedTriggerCache.set(triggerEvent, result);
    return result;
  } catch {
    return triggerEvent;
  }
}

/** Extract primitive value from field.value (unwrap { value: x } for executeAction payload). */
const extractFieldValue = (v) => {
  let current = v;
  while (current != null && typeof current === "object" && "value" in current && Object.keys(current).length <= 2) {
    current = current.value;
  }
  return current;
};

const VALID_EXECUTE_FIELD_IN = new Set(["path", "query", "body", "formData", "header", "cookie"]);

/** @deprecated use executeFieldNormalize — kept for a few inline callers */
const normalizeFieldIn = (raw) => {
  if (raw == null) return undefined;
  const s = String(raw).trim();
  if (!s) return undefined;
  const low = s.toLowerCase();
  if (low === "formdata") return "formData";
  if (VALID_EXECUTE_FIELD_IN.has(s)) return s;
  if (VALID_EXECUTE_FIELD_IN.has(low)) return low === "formdata" ? "formData" : low;
  return undefined;
};

/** @deprecated use executeFieldNormalize */
const fieldInFromDict = (f) => {
  if (!f || typeof f !== "object") return undefined;
  return normalizeFieldIn(f.in ?? f.paramIn ?? f.schema?.in);
};

/** @deprecated use executeFieldNormalize */
const getFieldIn = (f) => fieldInFromDict(f);

/**
 * Build trigger event payload for agent preview.
 * Uses fetchedPayload when provided (from createTrigger/getDD so preview mimics runtime);
 * otherwise uses trigger.test_data or trigger.fields or empty.
 */
const buildTriggerEvent = (agent, inputEvent = null, fetchedPayload = null) => {
  const trigger = (agent.triggers || [])[0];
  if (inputEvent) return inputEvent;
  if (fetchedPayload != null && typeof fetchedPayload === "object" && !Array.isArray(fetchedPayload)) {
    return {
      source: trigger?.source || "workflow_event",
      type: trigger?.type || trigger?.trigger_key || "test",
      payload: fetchedPayload,
    };
  }
  if (trigger && trigger.test_data && typeof trigger.test_data === "object") {
    return { source: trigger.source || "workflow_event", type: trigger.type || trigger.trigger_key || "test", payload: trigger.test_data };
  }
  if (trigger && (trigger.fields || []).length > 0) {
    return {
      source: trigger.source || "workflow_event",
      type: trigger.type || trigger.trigger_key || "test",
      payload: (trigger.fields || []).reduce((acc, f) => {
        if (f?.name != null) acc[f.name] = extractTriggerFieldValue(f?.value) ?? f?.value;
        return acc;
      }, {}),
    };
  }
  return { source: "manual", type: "preview", payload: {} };
};

/** API Gateway WebSocket frames are capped (~128KB). Full API outputs in prior_steps exceed that after a few steps. */
const PREVIEW_PRIOR_OUTPUT_MAX = 14000;
const PREVIEW_PRIOR_JSON_BUDGET = 88000;

/** True when output may contain an unprocessed file blob (mirrors backend output_has_file_blob). */
export function outputNeedsFileEnrichment(output) {
  if (output == null || typeof output !== "object") return false;
  if (Array.isArray(output)) return output.some(outputNeedsFileEnrichment);

  const fileDataKeys = new Set([
    "file", "data", "content", "base64", "body", "attachment",
    "filecontent", "file_content", "filedata", "file_data", "bytes", "binary",
    "payload", "document", "filebody", "file_body", "raw", "buffer",
  ]);
  const urlFileKeys = new Set([
    "url", "file_url", "fileurl", "download_url", "downloadurl", "href", "link",
    "attachment_url", "photo_url", "image_url", "document_url", "signed_url", "signedurl", "presigned_url",
  ]);
  const mimeKeys = new Set(["mimetype", "mime_type", "mimetype", "contenttype", "content_type", "media_type", "mediatype"]);
  const filenameKeys = new Set(["filename", "file_name", "filename", "name", "title"]);
  const looksLikeFileKey = (k) => /file|attachment|document|download|photo|image|pdf/i.test(k);
  const looksLikeHttpUrl = (s) => typeof s === "string" && /^https?:\/\/.{12,}/i.test(s.trim());
  const looksLikeBase64File = (val) => {
    if (typeof val !== "string") return false;
    const compact = val.replace(/\s+/g, "");
    if (compact.length < 200 || !/^[A-Za-z0-9+/=]+$/.test(compact.slice(0, 8192))) return false;
    try {
      const sample = compact.slice(0, 8192);
      const padded = sample + "=".repeat((4 - (sample.length % 4)) % 4);
      const bin = atob(padded);
      return bin.length >= 8;
    } catch {
      return false;
    }
  };

  const processedUrlKeys = new Set();
  const fe = output.file_extraction;
  if (fe && typeof fe === "object") {
    const extracted = String(fe.extracted_text || "").trim();
    if (extracted || fe.file_available_for_upload || fe.upload_ref) return false;
    const sourceKey = String(fe.source_key || "").trim().toLowerCase();
    if (sourceKey) processedUrlKeys.add(sourceKey);
  }

  for (const [key, val] of Object.entries(output)) {
    if (key === "_file_blobs") continue;
    const kl = key.toLowerCase();
    if (processedUrlKeys.has(kl)) continue;
    if (mimeKeys.has(kl) || filenameKeys.has(kl)) continue;
    if (typeof val === "string") {
      const isFileField =
        fileDataKeys.has(kl) ||
        urlFileKeys.has(kl) ||
        looksLikeFileKey(kl);
      if (!isFileField) continue;
      if (looksLikeBase64File(val)) return true;
      if (looksLikeHttpUrl(val) && (urlFileKeys.has(kl) || looksLikeFileKey(kl))) return true;
    } else if (val && typeof val === "object") {
      if (outputNeedsFileEnrichment(val)) return true;
    }
  }
  return false;
}

const pendingBedrockCalls = [];

function noteBedrockCalls(body) {
  const calls = body && body.bedrock_calls;
  if (Array.isArray(calls) && calls.length) pendingBedrockCalls.push(...calls);
}

function takePendingBedrockCalls() {
  return pendingBedrockCalls.splice(0, pendingBedrockCalls.length);
}

/** Decode base64 file downloads (e.g. ServiceTitan {"file": "..."}) before brain / truncation.
 *  Preserves `_file_blobs` so a later upload tool can hydrate prior_file:* refs.
 */
export async function enrichToolOutputForBrain(output) {
  if (output == null) return output;
  if (!outputNeedsFileEnrichment(output)) return output;
  const stashedBlobs = extractFileBlobsFromOutput(output);
  const brainPayload = { operation: "enrichToolOutput", output };
  const djangocallerPayload = { operation: "enrichToolOutput", data: { output } };
  const timeout = 300000; // match Lambda Function URL timeout (5 min)
  const targets = [];
  const fnUrl = (config.AGENT_FILE_EXTRACT_FUNCTION_URL || "").trim().replace(/\/$/, "");
  if (fnUrl) targets.push({ url: fnUrl, body: brainPayload, label: "lambda-function-url" });
  const direct = (config.EXECUTE_ACTION_DIRECT_URL || "").trim();
  if (direct) targets.push({ url: direct, body: djangocallerPayload, label: "django-direct" });
  const brain = (config.AGENT_BRAIN_HTTP_URL || "").trim();
  if (brain) targets.push({ url: brain, body: brainPayload, label: "agent-brain" });
  targets.push({
    url: `${DJANGOCALLER_API_BASE_URL}${DJANGOCALLER_PATH}`,
    body: djangocallerPayload,
    label: "djangocaller",
  });
  let lastErr = null;
  for (const t of targets) {
    try {
      const response = await axios.post(t.url, t.body, {
        timeout,
        headers: { "Content-Type": "application/json" },
      });
      let body = response?.data;
      if (body && body.body && body.output === undefined) {
        try {
          body = typeof body.body === "string" ? JSON.parse(body.body) : body.body;
        } catch (_) {
          /* keep original */
        }
      }
        if (body && body.output !== undefined) {
          if (body.ocr_debug && body.warning) {
            console.warn(`enrichToolOutput (${t.label}):`, body.warning, body.ocr_debug);
          }
          noteBedrockCalls(body);
          return attachFileBlobsForUpload(body.output, stashedBlobs);
        }
      lastErr = new Error(`${t.label}: missing output in response`);
    } catch (err) {
      lastErr = err;
      console.warn(`enrichToolOutput via ${t.label} failed:`, err?.message || err);
    }
  }
  console.warn("enrichToolOutput failed — brain may not read file downloads:", lastErr?.message || lastErr);
  return attachFileBlobsForUpload(output, stashedBlobs);
}

const AGENT_BRAIN_KEEP_TOP_LEVEL_KEYS = new Set([
  "page", "pageSize", "page_size", "hasMore", "has_more", "totalCount", "total_count",
  "count", "nextPage", "next_page", "cursor", "status", "success", "ok", "status_code",
  "error", "message", "warning", "warnings",
]);
const AGENT_BRAIN_PRIMARY_BODY_KEYS = [
  "data", "items", "results", "records", "rows", "jobs", "appointments", "customers",
  "technicians", "attachments", "files", "photos", "images", "media", "documents", "uploads",
];
const CATALOG_KEEP_KEYS = new Set([
  "id", "uuid", "guid", "key", "type", "name", "title", "filename", "file_name",
  "originalfilename", "original_file_name", "jobnumber", "job_number", "number",
  "mimetype", "mime_type", "contenttype", "content_type", "extension", "size",
  "filesize", "file_size", "status", "jobstatus", "job_status", "completedon",
  "completed_on", "createdon", "created_on", "modifiedon", "modified_on", "updatedon",
  "updated_on", "customerid", "customer_id", "locationid", "location_id",
  "businessunitid", "business_unit_id",
]);
const INDEXED_LIST_KEY_RE = /^([A-Za-z_][\w]*)_(\d+)$/;
const FILE_CATALOG_KEY_RE = /(attachment|file|photo|image|media|document|upload)s?$/i;
const AGENT_BRAIN_ID_LIST_MAX_ITEMS = 100;
const AGENT_BRAIN_LIST_PREVIEW_ITEMS = 8;

function compactRecordIdsForBrain(item) {
  if (item == null || typeof item !== "object" || Array.isArray(item)) {
    if (typeof item === "string" || typeof item === "number" || typeof item === "boolean" || item == null) {
      return item;
    }
    return String(item).slice(0, 120);
  }
  const out = {};
  for (const [k, v] of Object.entries(item)) {
    if (v == null || typeof v === "object") continue;
    const kl = String(k).toLowerCase();
    let keep = CATALOG_KEEP_KEYS.has(kl);
    if (!keep && (kl.includes("filename") || (kl.endsWith("name") && !["firstname", "lastname", "username"].includes(kl)))) {
      keep = typeof v === "string" && v.length < 240;
    }
    if (keep) {
      out[k] = typeof v === "string" && v.length > 240 ? `${v.slice(0, 220)}…` : v;
    }
  }
  if (Object.keys(out).length) return out;
  for (const [k, v] of Object.entries(item).slice(0, 6)) {
    if (typeof v === "string" || typeof v === "number" || typeof v === "boolean" || v == null) {
      out[k] = typeof v === "string" && v.length > 120 ? `${v.slice(0, 100)}…` : v;
    }
  }
  return Object.keys(out).length ? out : { _note: "row omitted (no scalar ids)" };
}

function listLooksLikeIdCatalog(data) {
  if (!Array.isArray(data) || !data.length) return false;
  const sample = data.slice(0, 8).filter((x) => x && typeof x === "object" && !Array.isArray(x));
  if (sample.length < Math.max(1, Math.min(3, data.length))) return false;
  let idish = 0;
  for (const row of sample) {
    const keys = Object.keys(row).map((k) => k.toLowerCase());
    if (keys.some((k) => k === "id" || k.endsWith("id") || ["uuid", "guid", "filename", "file_name", "name", "mimetype", "mime_type"].includes(k))) {
      idish += 1;
    }
  }
  return idish >= Math.max(1, Math.floor((sample.length + 1) / 2));
}

function dictLooksLikeIndexedList(data) {
  if (!data || typeof data !== "object" || Array.isArray(data)) return false;
  const keys = Object.keys(data);
  if (!keys.length) return false;
  if (keys.every((k) => /^\d+$/.test(k))) {
    const idxs = keys.map(Number).sort((a, b) => a - b);
    const start = idxs[0];
    return (start === 0 || start === 1) && idxs.every((v, i) => v === start + i);
  }
  const matched = [];
  for (const k of keys) {
    const m = INDEXED_LIST_KEY_RE.exec(k);
    if (!m) return false;
    matched.push({ prefix: m[1], idx: Number(m[2]) });
  }
  const prefixes = new Set(matched.map((m) => m.prefix));
  if (prefixes.size !== 1) return false;
  const idxs = matched.map((m) => m.idx).sort((a, b) => a - b);
  const start = idxs[0];
  return (start === 0 || start === 1) && idxs.every((v, i) => v === start + i);
}

function indexedDictToList(data) {
  const keys = Object.keys(data);
  if (keys.every((k) => /^\d+$/.test(k))) {
    return keys.sort((a, b) => Number(a) - Number(b)).map((k) => data[k]);
  }
  return keys
    .slice()
    .sort((a, b) => {
      const ma = INDEXED_LIST_KEY_RE.exec(a);
      const mb = INDEXED_LIST_KEY_RE.exec(b);
      return (ma ? Number(ma[2]) : 0) - (mb ? Number(mb[2]) : 0);
    })
    .map((k) => data[k]);
}

function coerceListish(data) {
  if (data && typeof data === "object" && !Array.isArray(data) && dictLooksLikeIndexedList(data)) {
    return indexedDictToList(data);
  }
  return data;
}

function previewListOrDict(data, maxItems, listKey = null) {
  data = coerceListish(data);
  if (Array.isArray(data)) {
    const n = data.length;
    const forceCatalog = !!(listKey && FILE_CATALOG_KEY_RE.test(String(listKey)));
    if (n && (forceCatalog || listLooksLikeIdCatalog(data))) {
      const cap = Math.max(maxItems, AGENT_BRAIN_ID_LIST_MAX_ITEMS);
      const compact = data.slice(0, cap).map(compactRecordIdsForBrain);
      const out = { _type: "list", count: n, items: compact };
      out._note = n > cap
        ? `${n - cap} more item(s) omitted — re-fetch or paginate; process every id in items before stopping`
        : `All ${n} item(s) included as id/name summaries — process every id before returning proposed_actions: []`;
      return out;
    }
    if (n <= maxItems) return data;
    return {
      _type: "list",
      count: n,
      preview: data.slice(0, maxItems),
      _note: `${n - maxItems} more item(s) omitted — continue with remaining on later turns if the goal requires all of them`,
    };
  }
  if (data && typeof data === "object") {
    const slim = {};
    const primaryLower = new Set(AGENT_BRAIN_PRIMARY_BODY_KEYS.map((k) => k.toLowerCase()));
    for (const [k, v] of Object.entries(data)) {
      if (AGENT_BRAIN_KEEP_TOP_LEVEL_KEYS.has(k) || v == null || typeof v !== "object") {
        slim[k] = v;
        continue;
      }
      if (primaryLower.has(String(k).toLowerCase()) || Array.isArray(v)) {
        slim[k] = previewListOrDict(v, maxItems, k);
      } else if (dictLooksLikeIndexedList(v)) {
        slim[k] = previewListOrDict(indexedDictToList(v), maxItems, k);
      } else {
        slim[k] = previewListOrDict(v, Math.max(2, Math.floor(maxItems / 2)), k);
      }
    }
    return slim;
  }
  return data;
}

function hasPrimaryApiBody(obj) {
  return AGENT_BRAIN_PRIMARY_BODY_KEYS.some(
    (k) => obj[k] != null && typeof obj[k] === "object",
  );
}

/**
 * Agent-facing tool output: drop workflow-editor `workload_response` list→dict bloat
 * and normalize indexed dicts back to arrays. Keeps the real API response shape.
 */
export function prepareApiOutputForAgentBrain(output) {
  if (output == null) return output;
  if (typeof output === "string") {
    const s = output.trim();
    if (s.startsWith("{") || s.startsWith("[")) {
      try {
        const parsed = JSON.parse(s);
        if (parsed && typeof parsed === "object") return prepareApiOutputForAgentBrain(parsed);
      } catch {
        return output;
      }
    }
    return output;
  }
  if (Array.isArray(output)) return output;
  if (typeof output !== "object") return output;

  const obj = { ...output };
  const wr = obj.workload_response ?? obj.workloadResponse;
  delete obj.workload_response;
  delete obj.workloadResponse;

  if (!hasPrimaryApiBody(obj) && wr && typeof wr === "object" && !Array.isArray(wr)) {
    for (const [k, v] of Object.entries(wr)) {
      if (obj[k] !== undefined) continue;
      obj[k] = coerceListish(v);
    }
  }

  for (const key of AGENT_BRAIN_PRIMARY_BODY_KEYS) {
    if (obj[key] && typeof obj[key] === "object" && !Array.isArray(obj[key])) {
      obj[key] = coerceListish(obj[key]);
    }
  }
  return obj;
}

function recoverIdsFromTruncatedJson(s) {
  if (typeof s !== "string" || !s) return null;
  const ids = [];
  const seen = new Set();
  const re = /"id"\s*:\s*(?:"([^"]+)"|(\d+))/g;
  let m;
  while ((m = re.exec(s))) {
    const raw = m[1] != null ? m[1] : m[2];
    if (raw == null || seen.has(raw)) continue;
    seen.add(raw);
    const n = Number(raw);
    ids.push(Number.isFinite(n) && String(n) === raw ? n : raw);
  }
  if (ids.length < 2) return null;
  return {
    _type: "list",
    count: ids.length,
    items: ids.map((id) => ({ id })),
    _note: `Recovered ${ids.length} id(s) from a truncated prior-step payload — this count is a lower bound; re-fetch or paginate if the goal needs every record`,
    _truncated_recovery: true,
  };
}

function shrinkCatalogToBudget(obj, maxChars) {
  const dump = (o) => JSON.stringify(o);
  let working;
  try {
    working = JSON.parse(JSON.stringify(obj));
  } catch {
    working = obj;
  }
  let t = dump(working);
  if (t.length <= maxChars) return t;

  const shrinkNode = (node) => {
    if (Array.isArray(node)) {
      for (const v of node) {
        if (shrinkNode(v)) return true;
      }
      return false;
    }
    if (!node || typeof node !== "object") return false;
    if (
      node._type === "list" &&
      Array.isArray(node.items) &&
      typeof node.count === "number" &&
      node.items.length > 1
    ) {
      const keep = Math.max(1, Math.floor(node.items.length / 2));
      node.items = node.items.slice(0, keep);
      node._note = `Showing ${keep} of ${node.count} id(s) for prompt size — process listed ids, then continue`;
      return true;
    }
    for (const v of Object.values(node)) {
      if (shrinkNode(v)) return true;
    }
    return false;
  };

  for (let i = 0; i < 24; i += 1) {
    t = dump(working);
    if (t.length <= maxChars) return t;
    if (!shrinkNode(working)) break;
  }

  const recovered = recoverIdsFromTruncatedJson(t);
  if (recovered) {
    let t2 = dump(recovered);
    if (t2.length <= maxChars) return t2;
    recovered.items = recovered.items.slice(0, Math.max(1, Math.floor(maxChars / 40)));
    recovered._note = `Prompt budget forced a shorter id list (${recovered.items.length} of ${recovered.count}); continue on later turns`;
    return dump(recovered);
  }
  return `${t.slice(0, Math.max(0, maxChars - 24))}\n… [truncated]`;
}

function structureToolOutputForBrain(output) {
  let obj = prepareApiOutputForAgentBrain(output);
  if (obj == null) return null;
  if (typeof obj === "string") {
    return recoverIdsFromTruncatedJson(obj) || obj;
  }
  if (Array.isArray(obj)) {
    return previewListOrDict(obj, AGENT_BRAIN_LIST_PREVIEW_ITEMS);
  }
  if (typeof obj !== "object") return obj;

  const preserved = {};
  if (obj.file_extraction && typeof obj.file_extraction === "object") {
    const fe = {};
    for (const [k, v] of Object.entries(obj.file_extraction)) {
      if (!["data", "base64", "content", "attachment", "file"].includes(k)) fe[k] = v;
    }
    preserved.file_extraction = fe;
  }
  if (obj.document_text) preserved.document_text = obj.document_text;

  const status = obj.status_code;
  if ((typeof status === "number" && status >= 400) || obj.error) {
    return {
      status_code: status,
      error: obj.error,
      message: obj.message,
      ...preserved,
    };
  }

  const slim = {};
  for (const [k, v] of Object.entries(obj)) {
    if (k in preserved || k === "workload_response" || k === "workloadResponse") continue;
    if (AGENT_BRAIN_KEEP_TOP_LEVEL_KEYS.has(k) || v == null || typeof v !== "object") {
      slim[k] = v;
    }
  }
  for (const key of AGENT_BRAIN_PRIMARY_BODY_KEYS) {
    if (obj[key] == null) continue;
    const val = obj[key];
    slim[key] = typeof val === "object"
      ? previewListOrDict(val, AGENT_BRAIN_LIST_PREVIEW_ITEMS, key)
      : val;
  }
  for (const [k, v] of Object.entries(obj)) {
    if (k in slim || k in preserved || k === "workload_response" || k === "workloadResponse") continue;
    if (v != null && typeof v === "object") {
      slim[k] = previewListOrDict(v, Math.max(2, Math.floor(AGENT_BRAIN_LIST_PREVIEW_ITEMS / 2)), k);
    } else {
      slim[k] = v;
    }
  }
  return { ...slim, ...preserved };
}

/** Catalogize list outputs + drop workload_response before WS/char budgets. */
export function summarizeToolOutputForBrain(output, maxChars = PREVIEW_PRIOR_OUTPUT_MAX) {
  if (output == null) return "";
  const structured = structureToolOutputForBrain(stripFileBlobsFromOutputForBrain(output));
  if (structured == null) return "";
  if (typeof structured === "string") {
    if (structured.length <= maxChars) return structured;
    const recovered = recoverIdsFromTruncatedJson(structured);
    if (recovered) return shrinkCatalogToBudget(recovered, maxChars);
    return `${structured.slice(0, Math.max(0, maxChars - 24))}\n… [truncated]`;
  }
  return shrinkCatalogToBudget(structured, maxChars);
}

function shrinkPreviewRowForWs(row) {
  if (!row || typeof row !== "object") return row;
  const next = {
    action_id: row.action_id,
    label: row.label || "",
    output: row.output && typeof row.output === "object" && !Array.isArray(row.output)
      ? { ...row.output }
      : row.output,
  };
  if (next.output && typeof next.output === "object" && !Array.isArray(next.output)) {
    delete next.output._file_blobs;
    if (typeof next.output.summary === "string" && next.output.summary.length > 280) {
      next.output.summary = `${next.output.summary.slice(0, 280)}…`;
    }
    if (next.output.structure != null) {
      next.output.structure = projectStructureForReceipt(next.output.structure);
    }
    if (next.output.file_extraction && typeof next.output.file_extraction === "object") {
      const fe = { ...next.output.file_extraction };
      delete fe._file_blobs;
      if (typeof fe.extracted_text === "string" && fe.extracted_text.length > 800) {
        fe.extracted_text = `${fe.extracted_text.slice(0, 800)}…`;
      }
      next.output.file_extraction = fe;
    }
  }
  if (row.receipt && typeof row.receipt === "object") {
    const receipt = { ...row.receipt };
    delete receipt._file_blobs;
    if (receipt.structure != null) receipt.structure = projectStructureForReceipt(receipt.structure);
    if (receipt.file && typeof receipt.file === "object") {
      const file = { ...receipt.file };
      delete file._file_blobs;
      if (typeof file.extracted_text_preview === "string" && file.extracted_text_preview.length > 800) {
        file.extracted_text_preview = `${file.extracted_text_preview.slice(0, 800)}…`;
      }
      receipt.file = file;
    }
    next.receipt = receipt;
  }
  return next;
}

/** Last-resort row: upload ref + short OCR, no structural dump. */
function tightenPreviewRow(row) {
  const summary = String(row?.output?.summary || row?.receipt?.summary || "OK").slice(0, 280);
  const fe = row?.output?.file_extraction;
  const output = {
    receipt: true,
    summary,
    ok: row?.output?.ok,
  };
  if (fe && typeof fe === "object" && fe.upload_ref) {
    output.file_extraction = {
      upload_ref: fe.upload_ref,
      mime_type: fe.mime_type,
      extraction_method: fe.extraction_method,
      file_available_for_upload: fe.file_available_for_upload,
      extracted_text: String(fe.extracted_text || "").slice(0, 400),
    };
  }
  return {
    action_id: row?.action_id,
    label: row?.label || "",
    output,
  };
}

function truncatePriorStepsForPreviewWs(priorSteps) {
  if (!Array.isArray(priorSteps) || priorSteps.length === 0) return [];
  // Receipt-first: never ship raw download blobs over the websocket.
  let rows = priorSteps.map((s, i) => {
    const receiptStep = receiptFromPriorStep(s, i);
    return shrinkPreviewRowForWs({
      action_id: receiptStep.action_id,
      label: receiptStep.label || "",
      output: receiptStep.output,
      receipt: receiptStep.receipt,
    });
  });
  let json = JSON.stringify(rows);
  while (json.length > PREVIEW_PRIOR_JSON_BUDGET && rows.length > 1) {
    rows = rows.slice(1);
    json = JSON.stringify(rows);
  }
  if (json.length > PREVIEW_PRIOR_JSON_BUDGET && rows.length === 1) {
    rows = [tightenPreviewRow(rows[0])];
  }
  return rows;
}

/** Whole preview frame, including the agent definition, must stay under 128KB. */
function fitPreviewMessageForWs(msg) {
  if (!msg || typeof msg !== "object") return msg;
  if (messageUtf8Bytes(msg) <= PREVIEW_WS_FRAME_BUDGET) return msg;
  let steps = Array.isArray(msg.prior_steps) ? msg.prior_steps.slice() : [];
  let fitted = { ...msg, prior_steps: steps };
  while (steps.length > 1 && messageUtf8Bytes(fitted) > PREVIEW_WS_FRAME_BUDGET) {
    steps = steps.slice(1);
    fitted = { ...msg, prior_steps: steps };
  }
  if (messageUtf8Bytes(fitted) > PREVIEW_WS_FRAME_BUDGET && steps.length) {
    fitted = { ...msg, prior_steps: [tightenPreviewRow(steps[steps.length - 1])] };
  }
  if (messageUtf8Bytes(fitted) > PREVIEW_WS_FRAME_BUDGET) {
    const { prior_steps, ...rest } = fitted;
    fitted = rest;
  }
  return fitted;
}

/**
 * Send agent preview request via WebSocket (initial or next step). Optional priorSteps and userMessage
 * for multi-step / chat. Returns { run_id, event, reasoning, proposed_actions, from_brain, error? }.
 */
const sendPreviewRequest = (agent, triggerEvent, { priorSteps = [], userMessage = "", browserSessionReuse = null } = {}) => {
  const wsEndpoint = config.AGENTS_WS_ENDPOINT || config.WS_AGENT_ENDPOINT;
  if (!wsEndpoint) return Promise.resolve(null);

  const runId = `preview_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
  const payload = triggerEvent?.payload ?? triggerEvent ?? {};

  // Brain + long photo pipelines often exceed 2 min; match Lambda Function URL budget (5 min).
  const timeoutMs =
    Array.isArray(priorSteps) && priorSteps.length > 0 ? 300000 : 90000;
  return new Promise((resolve) => {
    const timeout = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        resolve(null);
      }
      try {
        if (ws) ws.close();
      } catch (_) {}
    }, timeoutMs);
    let resolved = false;
    let ws;
    try {
      ws = new WebSocket(wsEndpoint);
      ws.onopen = () => {
        let previewUserId = "";
        let previewTenantId = "";
        try {
          previewUserId = localStorage.getItem("user") || "";
          previewTenantId =
            localStorage.getItem("ten_") || localStorage.getItem("tenant") || "";
        } catch (_) {
          /* ignore */
        }
        const msg = {
          action: "run_agent_preview",
          user_id: previewUserId || undefined,
          tenant_id: previewTenantId || undefined,
          agent: {
            goal: agent.goal,
            instructions: agent.instructions,
            rules: agent.rules,
            actions: agent.actions || [],
            policy: agent.policy || undefined,
            created_by_user_id:
              agent.created_by_user_id || previewUserId || undefined,
            // Must match executeAction: memory field aliases (jobId→id) apply at propose + execute.
            run_memory: agent.run_memory || [],
          },
          triggerEvent: stripFileBlobsFromOutputForBrain(payload),
        };
        if (Array.isArray(priorSteps) && priorSteps.length > 0) {
          msg.prior_steps = truncatePriorStepsForPreviewWs(priorSteps);
        }
        if (userMessage && String(userMessage).trim()) msg.user_message = String(userMessage).trim();
        const frame = fitPreviewMessageForWs(msg);
        ws.send(JSON.stringify(frame));
      };
      ws.onmessage = (event) => {
        if (resolved) return;
        let data;
        try {
          data = JSON.parse(event.data);
        } catch (_) {
          return;
        }
        const type = data.type || data.messageType;
        // Async Lambda fork sends "thinking" first — keep waiting for agent_preview.
        if (type === "thinking" || type === "status" || type === "progress") {
          return;
        }
        if (type === "agent_preview") {
          resolved = true;
          clearTimeout(timeout);
          try {
            ws.close();
          } catch (_) {}
          const payload = {
            run_id: runId,
            event: triggerEvent,
            reasoning: data.reasoning || "",
            proposed_actions: syncProposedActionsWithExecuteFields(
              agent,
              data.proposed_actions || [],
              {
                priorSteps,
                browserSessionReuse,
              },
            ),
            from_brain: true,
          };
          if (data.error) payload.error = data.error;
          if (Array.isArray(data.bedrock_calls) && data.bedrock_calls.length) {
            payload.bedrock_calls = data.bedrock_calls;
          }
          if (data.instruction_update) payload.instruction_update = data.instruction_update;
          if (data.rules_update) payload.rules_update = data.rules_update;
          if (Array.isArray(data.memory_updates) && data.memory_updates.length > 0) {
            payload.memory_updates = data.memory_updates;
          }
          resolve(payload);
        } else if (type === "agent_preview_error") {
          resolved = true;
          clearTimeout(timeout);
          try {
            ws.close();
          } catch (_) {}
          resolve({
            run_id: runId,
            event: triggerEvent,
            reasoning: "",
            proposed_actions: [],
            from_brain: true,
            error: data.error || "Preview error",
          });
        }
      };
      ws.onerror = () => {
        if (!resolved) {
          resolved = true;
          clearTimeout(timeout);
          resolve(null);
        }
      };
      ws.onclose = () => {
        if (!resolved) {
          resolved = true;
          clearTimeout(timeout);
          resolve(null);
        }
      };
    } catch (_) {
      clearTimeout(timeout);
      resolve(null);
    }
  });
};

/**
 * Run the agent brain via WebSocket: trigger + agent -> reasoning + proposed_actions.
 * Uses same Bedrock model as AI draft. Falls back to null if WS unavailable or error.
 */
export const previewAgentWithBrain = async (agent, inputEvent = null, options = null) => {
  const onPhase = typeof options?.onPhase === "function" ? options.onPhase : null;
  const triggerEvent = buildTriggerEvent(agent, inputEvent);
  onPhase?.(
    "enrich_files",
    "Reading attachments and documents… This can take a minute for PDFs.",
  );
  const enrichedEvent = await enrichTriggerEventForBrain(triggerEvent);
  onPhase?.("brain", "Planning actions with AI…");
  return sendPreviewRequest(agent, enrichedEvent, {});
};

/**
 * Request next step in preview: same brain with prior step outputs and optional user message.
 * priorSteps: [{ action_id, label, output }]. Returns same shape as previewAgentWithBrain.
 */
export const previewAgentNextStep = async (
  agent,
  triggerEvent,
  { priorSteps = [], userMessage = "", onPhase } = {},
) => {
  const report = typeof onPhase === "function" ? onPhase : null;
  report?.(
    "enrich_files",
    "Reading attachments and documents… This can take a minute for PDFs.",
  );
  const enrichedEvent = await enrichTriggerEventForBrain(triggerEvent);
  report?.(
    "brain",
    userMessage?.trim() ? "Updating plan from your reply…" : "Planning next actions with AI…",
  );
  return sendPreviewRequest(agent, enrichedEvent, { priorSteps, userMessage });
};

/**
 * Compute proposed_actions from agent (static list, no LLM). Used when brain is unavailable.
 */
const computeProposedActions = (agent, inputEvent = null) => {
  const runId = `preview_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
  const event = buildTriggerEvent(agent, inputEvent);

  const enabledRules = (agent.rules || []).filter((r) => r && r.enabled !== false);
  const actionIds = new Set();
  enabledRules.forEach((rule) => (rule.action_ids || []).forEach((id) => actionIds.add(id)));
  const actionMap = {};
  (agent.actions || []).forEach((a) => {
    if (a?.action_id) actionMap[a.action_id] = a;
  });
  const proposed = [];
  const seen = new Set();
  enabledRules.forEach((rule) => {
    (rule.action_ids || []).forEach((actionId) => {
      const action = actionMap[actionId];
      if (!action || seen.has(action.action_id)) return;
      seen.add(action.action_id);
      const fields = action?.config?.fields || action?.fields || [];
      const payload_preview = [];
      (Array.isArray(fields) ? fields : []).forEach((f) => {
        if (!f?.name) return;
        const v = extractFieldValue(f?.value);
        if (v === undefined || v === null || v === "" || String(v) === "Let agent pick value") return;
        // Keep objects/arrays structured so Preview CollapsibleValueView can expand them.
        // Do not JSON.stringify — that flattens the tree into one opaque string.
        payload_preview.push({ name: f.name || f.title, value: v });
      });
      proposed.push({
        action_id: action.action_id,
        label: action.label || action.action_label || action.type,
        app_label: action.app_label || action.app,
        app_icon: action.app_icon,
        rule_name: rule.name || "",
        payload_preview,
      });
    });
  });
  return { run_id: runId, event, proposed_actions: proposed, from_brain: false };
};

/**
 * Rewrite proposed field names from agent.run_memory immediately before executeAction.
 * Memory wins only when rewriting TO a catalog name (or when the proposed name is not
 * in the catalog). Never rewrite a real catalog field to a non-catalog alias — that
 * caused Preview to show jobId while executeAction sent id (and KeyError: jobId loops).
 */
function applyRunMemoryFieldHints(action, fields, runMemory) {
  if (!Array.isArray(fields) || fields.length === 0) return fields || [];
  const catalog = catalogFieldsByName(action);
  const canon = {};
  Object.keys(catalog).forEach((k) => {
    const af = catalog[k];
    const n = String(af?.name || af?.title || "").trim();
    if (n) canon[n.toLowerCase()] = n;
  });
  const aid = String(action?.action_id || "");
  const rows = (Array.isArray(runMemory) ? runMemory : []).filter(
    (m) => m && String(m.action_id || "") === aid
  );
  const aliases = {};
  const quoted = /['"]([A-Za-z_][\w]*)['"]\s+not\s+['"]([A-Za-z_][\w]*)['"]/gi;
  const loose =
    /(?:parameter name is|field(?: parameter)? name is|name is|always use|must (?:be|use)|use)\s+['"]?([A-Za-z_][\w]*)['"]?\s+not\s+['"]?([A-Za-z_][\w]*)['"]?/gi;
  const remember = (wrong, right) => {
    if (!wrong || !right) return;
    if (String(wrong).toLowerCase() === String(right).toLowerCase()) return;
    aliases[String(wrong).toLowerCase()] = right;
  };
  for (const row of rows) {
    const lesson = String(row.lesson || "");
    let m;
    quoted.lastIndex = 0;
    while ((m = quoted.exec(lesson))) remember(m[2], m[1]);
    loose.lastIndex = 0;
    while ((m = loose.exec(lesson))) remember(m[2], m[1]);
    for (const h of row.fields_hint || []) {
      if (String(h?.name || "").toLowerCase() === "id" && canon.id && !canon.jobid) {
        remember("jobId", h.name);
        remember("job_id", h.name);
        remember("jobid", h.name);
      }
    }
  }
  // Catalog fallback (same as backend): jobId → id when the action only defines id.
  if (!aliases.jobid && canon.id && !canon.jobid) {
    remember("jobId", canon.id);
    remember("job_id", canon.id);
  }
  if (!Object.keys(aliases).length) return fields;
  const used = new Set();
  const out = [];
  for (const f of fields) {
    if (!f || typeof f !== "object") continue;
    const row = { ...f };
    let name = String(row.name || row.title || "").trim();
    if (!name) continue;
    const mapped = aliases[name.toLowerCase()];
    if (mapped && mapped.toLowerCase() !== name.toLowerCase()) {
      const wrongInCatalog = Boolean(canon[name.toLowerCase()]);
      const rightInCatalog = Boolean(canon[String(mapped).toLowerCase()]);
      // Never rewrite a catalog field to a name the action does not define.
      if (!(wrongInCatalog && !rightInCatalog)) {
        row.name = mapped;
        name = mapped;
      }
    }
    const key = String(row.name || "").toLowerCase();
    if (used.has(key)) continue;
    used.add(key);
    out.push(row);
  }
  return out;
}

/** Keep payload_preview in sync with the fields that executeAction will actually send. */
function rebuildPayloadPreviewFromFields(fields) {
  const payload_preview = [];
  (Array.isArray(fields) ? fields : []).forEach((f) => {
    if (!f || typeof f !== "object") return;
    const name = f.name || f.title;
    if (!name) return;
    let val = f.value;
    if (typeof val === "string") {
      const s = val.trim();
      if (s.startsWith("{") || s.startsWith("[")) {
        try {
          const parsed = JSON.parse(s);
          if (parsed && typeof parsed === "object") val = parsed;
        } catch (_) {
          /* keep string */
        }
      }
    }
    if (name === "body" && val && typeof val === "object" && !Array.isArray(val)) {
      Object.keys(val).forEach((propKey) => {
        payload_preview.push({ name: `body.${propKey}`, in: f.in, value: val[propKey] });
      });
    } else {
      payload_preview.push({ name, in: f.in, value: val });
    }
  });
  return payload_preview;
}

/**
 * Build the exact fields[] that executeAction / djangocaller will receive.
 * Preview "Data to be sent" and the network payload must both use this.
 */
export function buildExecuteFieldsForProposedAction(action, proposed, agent = null, options = null) {
  if (!action) return [];
  const catalog = catalogFieldsByName(action);
  const toExecuteRow = (f, valueOverride) => {
    const name = f?.name || f?.title;
    if (!name) return null;
    const catalogField = catalog[name] || catalog[String(name).toLowerCase()];
    const rawVal = valueOverride !== undefined ? valueOverride : f?.value;
    const inv = resolveFieldIn(f, catalogField);
    const value = serializeExecuteFieldValue(name, inv, rawVal, catalogField);
    return { name, in: inv, value };
  };

  let fields;
  const proposedFields = proposed && Array.isArray(proposed.fields) ? proposed.fields : null;
  if (proposedFields && proposedFields.length > 0) {
    const rewritten = applyRunMemoryFieldHints(action, proposedFields, agent?.run_memory);
    let rows = rewritten
      .map((f) => {
        const name = f?.name || f?.title;
        if (!name) return null;
        const cat = catalog[name] || catalog[String(name).toLowerCase()];
        return normalizeExecuteFieldRow(f, cat);
      })
      .filter(Boolean);

    // Workload Code: keep configured python_code; brain only fills inputs.
    const appSlug = String(action.app || action.app_label || "").toLowerCase();
    const actionKey = String(
      action.action_key || action.config?.action || action.type || ""
    ).toLowerCase();
    if (appSlug === "workload-code" && actionKey === "runpython") {
      const configured = (action?.config?.fields || action?.fields || []).find(
        (f) => String(f?.name || "").toLowerCase() === "python_code"
      );
      const codeVal = configured?.value;
      if (codeVal != null && String(codeVal).trim() && String(codeVal).trim() !== "Let agent pick value") {
        rows = rows.filter((r) => String(r?.name || "").toLowerCase() !== "python_code");
        const cat = catalog.python_code || catalog.Python_code;
        const codeRow = normalizeExecuteFieldRow({ name: "python_code", value: codeVal }, configured || cat);
        if (codeRow) rows.push(codeRow);
      }
    }

    // Custom Request: keep configured HTTP Request structure; merge dynamic brain slots.
    if (actionKey === "custom request" || actionKey === "customrequest") {
      const configured = (action?.config?.fields || action?.fields || []).find(
        (f) => String(f?.name || "").toLowerCase() === "http request"
      );
      let base =
        configured?.value && typeof configured.value === "object" ? { ...configured.value } : null;
      if (base) {
        const brainFull = rows.find((r) => String(r?.name || "").toLowerCase() === "http request");
        if (brainFull && brainFull.value && typeof brainFull.value === "object") {
          const incoming = brainFull.value;
          for (const k of ["params", "headers", "stringBody"]) {
            if (incoming[k] != null) base[k] = incoming[k];
          }
          if (incoming.url && (!base.url || String(base.url) === "Let agent pick value")) {
            base.url = incoming.url;
          }
          if (incoming.requestType && !base.requestType) base.requestType = incoming.requestType;
        }
        const paramsRow = rows.find((r) => String(r?.name || "").toLowerCase() === "http request.params");
        if (paramsRow) base.params = paramsRow.value;
        const bodyRow = rows.find((r) => String(r?.name || "").toLowerCase() === "http request.stringbody");
        if (bodyRow) base.stringBody = bodyRow.value;
        const docs =
          action?.apiDocs ||
          action?.config?.apiDocs ||
          action?.apiDocsUrl ||
          action?.config?.apiDocsUrl ||
          "";
        const verifiedBase =
          action?.verifiedApiBase ||
          action?.apiBaseUrl ||
          action?.config?.verifiedApiBase ||
          action?.config?.apiBaseUrl ||
          "";
        base = normalizeCustomRequestHttpValue(base, {
          apiBaseUrl: verifiedBase,
          verifiedApiBase: verifiedBase,
          apiDocs: docs,
        });
        base = shapeCustomRequestHttpValueForExecute(base);
        rows = rows.filter(
          (r) =>
            !["http request", "http request.params", "http request.stringbody"].includes(
              String(r?.name || "").toLowerCase()
            )
        );
        const cat = catalog["HTTP Request"] || catalog["http request"] || configured;
        const httpRow = normalizeExecuteFieldRow({ name: "HTTP Request", value: base }, cat);
        if (httpRow) {
          httpRow.required = true;
          rows.push(httpRow);
        }
      }
    }

    fields = coalesceExecuteFieldsForAction(action, rows)
      .map((f) => toExecuteRow(f, f.value))
      .filter(Boolean);
  } else {
    const actionFields = action?.config?.fields || action?.fields || [];
    fields = (Array.isArray(actionFields) ? actionFields : [])
      .map((f) => {
        if (!f?.name) return null;
        let v = extractFieldValue(f?.value);
        if (v === undefined || v === null || v === "" || String(v) === "Let agent pick value") return null;
        if (
          String(f.name).toLowerCase() === "http request" &&
          v &&
          typeof v === "object" &&
          !Array.isArray(v)
        ) {
          v = shapeCustomRequestHttpValueForExecute(v);
        }
        return toExecuteRow(f, v);
      })
      .filter(Boolean);
  }

  // Browser RPA: inject loginUrl / profileId / loginRecipe / session reuse.
  {
    const opKey = String(
      action.action_key || action.config?.action || action.type || (proposed && proposed.action_key) || ""
    )
      .toLowerCase()
      .replace(/_/g, "")
      .replace(/-/g, "")
      .replace(/\s/g, "");
    const isBrowserRpa =
      opKey === "browserrpa" ||
      Boolean(action.config?.browserRpa) ||
      Boolean(action.browserRpa) ||
      ["browser_rpa", "browserrpa", "browser-rpa"].includes(
        String(action.config?.automationMode || action.automationMode || "").toLowerCase(),
      );
    if (isBrowserRpa) {
      const have = new Set((fields || []).map((f) => String(f?.name || "").toLowerCase()));
      const appKey = String(
        action.app || action.app_label || action.passportApp || action.config?.app || "",
      )
        .trim()
        .toLowerCase();
      const sibling = (agent?.actions || []).find((a) => {
        if (!a || a.action_id === action.action_id) return false;
        const aKey = String(a.app || a.app_label || a.passportApp || a.config?.app || "")
          .trim()
          .toLowerCase();
        if (!appKey || aKey !== appKey) return false;
        const k = String(a.action_key || a.config?.action || a.type || "")
          .toLowerCase()
          .replace(/[_\-\s]/g, "");
        return (
          k === "browserrpa" ||
          Boolean(a.config?.browserRpa) ||
          Boolean(a.config?.profileId) ||
          Boolean(a.config?.loginRecipe)
        );
      });
      const recipe =
        action.config?.loginRecipe ||
        action.loginRecipe ||
        (typeof proposed?.loginRecipe === "object" ? proposed.loginRecipe : null) ||
        options?.browserSessionReuse?.loginRecipe ||
        sibling?.config?.loginRecipe ||
        null;
      const loginUrl =
        action.config?.loginUrl ||
        recipe?.loginUrl ||
        recipe?.url ||
        sibling?.config?.loginUrl ||
        "";
      const reuse = options?.browserSessionReuse || null;
      const reuseSid = String(reuse?.sessionId || "").trim();
      if (reuseSid && !have.has("sessionid")) {
        fields = [
          ...(fields || []).filter(
            (f) =>
              !["sessionid", "session_id", "resume", "reusesession"].includes(
                String(f?.name || "").toLowerCase(),
              ),
          ),
          { name: "sessionId", in: "path", value: reuseSid },
          { name: "resume", in: "path", value: true },
          { name: "reuseSession", in: "path", value: true },
        ];
        have.add("sessionid");
        have.add("resume");
      }
      if (loginUrl && !have.has("loginurl")) {
        fields = [...(fields || []), { name: "loginUrl", in: "path", value: loginUrl }];
        have.add("loginurl");
      }
      if (recipe && !have.has("loginrecipe")) {
        fields = [...(fields || []), { name: "loginRecipe", in: "path", value: recipe }];
        have.add("loginrecipe");
      }
      const profileId =
        action.config?.profileId ||
        action.config?.browserProfileId ||
        action.profileId ||
        reuse?.profileId ||
        sibling?.config?.profileId ||
        sibling?.config?.browserProfileId ||
        "";
      if (profileId && !have.has("profileid")) {
        fields = [...(fields || []), { name: "profileId", in: "path", value: profileId }];
      }
    }
  }

  // Resolve prior_file:* / omitted placeholders from a prior download step.
  const priorForFiles = options?.priorSteps || options?.prior_steps || [];
  const fileResolver = options?._priorFileResolver || null;
  if (Array.isArray(priorForFiles) && priorForFiles.length && Array.isArray(fields) && fields.length) {
    fields = hydrateProposedFieldsWithPriorFiles(fields, priorForFiles, fileResolver);
  }

  return fields || [];
}

/**
 * Preview/UI Approve is the human gate for pages.publish.
 * The model must not invent user_approved=true; clicking Approve (or an
 * equivalent explicit publish approval) injects it here at execute time.
 */
export function applyHumanPublishApprovalToFields(operation, fields) {
  const op = String(operation || "")
    .toLowerCase()
    .replace(/_/g, ".")
    .replace(/-/g, ".");
  const compact = op.replace(/[^a-z0-9]/g, "");
  if (compact !== "pagespublish") {
    return Array.isArray(fields) ? fields : [];
  }
  const next = Array.isArray(fields)
    ? fields.map((f) => (f && typeof f === "object" ? { ...f } : f))
    : [];
  let found = false;
  for (const f of next) {
    if (!f || typeof f !== "object") continue;
    const name = String(f.name || "").toLowerCase();
    if (name === "user_approved" || name === "userapproved" || name === "approved") {
      f.value = true;
      found = true;
    }
  }
  if (!found) {
    next.push({ name: "user_approved", value: true, in: "path" });
  }
  return next;
}

/**
 * Align proposed_actions.fields + payload_preview with the exact executeAction payload
 * so Preview "Data to be sent" matches the network / backend request.
 */
export function syncProposedActionsWithExecuteFields(agent, proposedActions, options = null) {
  if (!Array.isArray(proposedActions) || !proposedActions.length) return proposedActions || [];
  const actionMap = {};
  (agent?.actions || []).forEach((a) => {
    if (a?.action_id) actionMap[a.action_id] = a;
  });

  let syncOptions = options || null;
  const priorForFiles = syncOptions?.priorSteps || syncOptions?.prior_steps || [];
  if (Array.isArray(priorForFiles) && priorForFiles.length && !syncOptions?._priorFileResolver) {
    const annotatedPriors = assignStepUploadRefs(priorForFiles);
    syncOptions = {
      ...(syncOptions || {}),
      priorSteps: annotatedPriors,
      _priorFileResolver: createPriorFileBlobResolver(annotatedPriors),
    };
  }

  return proposedActions.map((p) => {
    if (!p || typeof p !== "object") return p;
    const action = actionMap[p.action_id];
    if (!action) return p;
    const fields = buildExecuteFieldsForProposedAction(action, p, agent, syncOptions);
    return {
      ...p,
      fields,
      payload_preview: rebuildPayloadPreviewFromFields(fields),
    };
  });
}

/**
 * Call djangocaller executeAction for each approved action (used when AGENTS_EXECUTE_VIA_HTTP or http mode).
 */
/**
 * Execute approved actions via djangocaller. When proposedActionsFromPreview is provided (from
 * agent brain preview), use each proposed action's .fields for the payload; otherwise use
 * agent.actions config.
 */
const executeViaDjangocaller = async (
  agentId,
  runId,
  approvedSelection,
  agent = null,
  proposedActionsFromPreview = null,
  options = null,
) => {
  const useProposalIndices =
    options &&
    options.useProposalIndices === true &&
    Array.isArray(proposedActionsFromPreview) &&
    proposedActionsFromPreview.length > 0;

  let resolvedAgent = agent || (await httpClient.get(`/agents/${agentId}`).then((r) => r.data).catch(() => null));
  if (!resolvedAgent) resolvedAgent = loadAgents().find((item) => item.agent_id === agentId) || null;
  if (!resolvedAgent) throw new Error("Agent not found.");
  const rawTenant = typeof localStorage !== "undefined" ? localStorage.getItem("tenant") : null;
  const rawUser = typeof localStorage !== "undefined" ? localStorage.getItem("user") : null;
  const tenantId = (rawTenant != null && String(rawTenant) !== "null" && String(rawTenant) !== "undefined") ? String(rawTenant) : "";
  const userId = (rawUser != null && String(rawUser) !== "null" && String(rawUser) !== "undefined") ? String(rawUser) : "";
  const actionMap = {};
  (resolvedAgent.actions || []).forEach((a) => {
    if (a?.action_id) actionMap[a.action_id] = a;
  });
  /** When not using indices, last duplicate action_id in proposed list would win — avoid that for legacy path */
  const proposedByActionId = {};
  if (Array.isArray(proposedActionsFromPreview) && !useProposalIndices) {
    proposedActionsFromPreview.forEach((p) => {
      if (p?.action_id) proposedByActionId[p.action_id] = p;
    });
  }
  const results = [];
  let firstError = null;

  const runCollection = options?.agent_run_collection || config.AGENT_RUN_MONGO_COLLECTION || "agent_tasks";
  const agentRunKind =
    options?.agent_run_kind || (String(runId).startsWith("preview_") ? "preview" : "live");

  // One resolver for the whole approved batch so N Drive uploads map to N distinct downloads.
  // Same options feed syncProposedActions + runOne so Preview and network payloads match.
  const priorForFiles = options?.priorSteps || options?.prior_steps || [];
  if (Array.isArray(priorForFiles) && priorForFiles.length) {
    const annotatedPriors = assignStepUploadRefs(priorForFiles);
    options = {
      ...(options || {}),
      priorSteps: annotatedPriors,
      _priorFileResolver: createPriorFileBlobResolver(annotatedPriors),
    };
  }
  // Re-sync with execute options (prior files, browser session) so fields[] match the wire payload.
  if (Array.isArray(proposedActionsFromPreview) && proposedActionsFromPreview.length > 0) {
    proposedActionsFromPreview = syncProposedActionsWithExecuteFields(
      resolvedAgent,
      proposedActionsFromPreview,
      options,
    );
    if (!useProposalIndices) {
      Object.keys(proposedByActionId).forEach((k) => delete proposedByActionId[k]);
      proposedActionsFromPreview.forEach((p) => {
        if (p?.action_id) proposedByActionId[p.action_id] = p;
      });
    }
  }

  /**
   * Pass-through fields for Mongo agent-run logging inside execute_action_task (see agent_run_logging.py).
   * Reasoning steps and finalize_agent_run must run in the backend orchestrator (Celery/Lambda/brain),
   * not in the browser — live/scheduled runs have no client.
   */
  const baseMeta = {
    agent_task_id: runId,
    run_id: runId,
    agent_id: agentId,
    tenant_id: tenantId,
    agent_run_kind: agentRunKind,
    agent_run_collection: runCollection,
  };

  const runOne = (actionId, proposed) => {
    // Brain may propose skills/pages tools injected at plan time even when they
    // were never saved on agent.actions — synthesize from the proposal so Approve
    // actually executes instead of silently no-oping and re-proposing the same step.
    let action = actionMap[actionId];
    if (!action && proposed && typeof proposed === "object") {
      const app =
        proposed.app ||
        proposed.app_label ||
        (proposed.config && proposed.config.app) ||
        "";
      const operation =
        proposed.action_key ||
        (proposed.config && proposed.config.action) ||
        proposed.type ||
        "";
      if (app || operation) {
        action = {
          action_id: actionId,
          app,
          app_label: proposed.app_label || app,
          action_key: operation,
          type: proposed.type || "run_workflow_step",
          label: proposed.label || operation,
          url: proposed.url || "",
          fields: Array.isArray(proposed.fields) ? proposed.fields : [],
          credential: proposed.credential || (proposed.config && proposed.config.credential) || "",
          config: {
            app,
            action: operation,
            fields: Array.isArray(proposed.fields) ? proposed.fields : [],
            ...(proposed.config && typeof proposed.config === "object" ? proposed.config : {}),
          },
        };
        actionMap[actionId] = action;
      }
    }
    if (!action) {
      const errMsg = `Approved action "${actionId}" is not on this agent and could not be synthesized from the proposal.`;
      results.push({
        action_id: actionId,
        success: false,
        error: errMsg,
        data: { error: errMsg, status_code: 400 },
      });
      if (!firstError) firstError = errMsg;
      return;
    }
    // Use fields already aligned by syncProposedActionsWithExecuteFields so the
    // network payload is identical to Preview "Data to be sent". Rebuild only when
    // there is no proposed action (static agent.actions path).
    let fields =
      proposed && Array.isArray(proposed.fields) && proposed.fields.length > 0
        ? proposed.fields
        : buildExecuteFieldsForProposedAction(action, proposed, resolvedAgent, options);

    const operation =
      action.action_key ||
      action.config?.action ||
      action.type ||
      (proposed && proposed.action_key) ||
      "";
    // Human clicked Approve on this mutating step → supply pages.publish gate.
    fields = applyHumanPublishApprovalToFields(operation, fields);

    const badRefs = unresolvedPriorFileRefs(fields);
    if (badRefs.length) {
      const errMsg =
        "Could not resolve file upload ref(s) to downloaded bytes: " +
        badRefs.slice(0, 4).join(", ") +
        ". Re-download the file and use that step's exact file_extraction.upload_ref.";
      results.push({
        action_id: actionId,
        success: false,
        error: errMsg,
        data: { error: errMsg, status_code: 400 },
      });
      if (!firstError) firstError = errMsg;
      return;
    }

    const data = {
      tenant_id: tenantId,
      user_id: userId,
      app: action.app || action.app_label || (proposed && proposed.app) || "",
      operation,
      url: (proposed && proposed.url) || action.url || "",
      fields,
      credential: action.credential ?? action.config?.credential ?? "",
      run_id: runId,
      agent_id: agentId,
      action_id: action.action_id,
      ...baseMeta,
    };
    // Propagate skills opt-in so Django/Lambda execute_action gate matches brain.
    const pol =
      resolvedAgent?.policy && typeof resolvedAgent.policy === "object"
        ? resolvedAgent.policy
        : {};
    if (pol.skills_consumer_enabled === true) {
      data.skills_consumer_enabled = true;
      data.agent = {
        agent_id: agentId,
        policy: pol,
        created_by_user_id: resolvedAgent.created_by_user_id || userId,
      };
    }
    return { actionId, data };
  };

  /** Build planned tool calls so each executeAction can include batch position for backend finalize. */
  const planned = [];
  if (useProposalIndices) {
    for (const rawIdx of approvedSelection || []) {
      const idx = typeof rawIdx === "number" ? rawIdx : parseInt(String(rawIdx), 10);
      if (!Number.isFinite(idx) || idx < 0 || idx >= proposedActionsFromPreview.length) continue;
      const proposed = proposedActionsFromPreview[idx];
      const actionId = proposed?.action_id;
      if (!actionId) continue;
      const pair = runOne(actionId, proposed);
      if (pair) planned.push({ pair, actionId, proposal_index: idx });
    }
  } else {
    for (const actionId of approvedSelection || []) {
      const proposed = proposedByActionId[actionId];
      const pair = runOne(actionId, proposed);
      if (pair) planned.push({ pair, actionId });
    }
  }

  const nSteps = planned.length;
  const reasoningForRun =
    options && typeof options.agent_reasoning === "string" ? options.agent_reasoning.trim() : "";
  const bedrockCallsForRun = []
    .concat(Array.isArray(options?.bedrock_calls) ? options.bedrock_calls : [])
    .concat(takePendingBedrockCalls());

  // Nothing executable was planned (e.g. all approved ids missing from agent) —
  // fail loudly instead of reporting success and re-proposing the same step.
  if (
    nSteps === 0 &&
    Array.isArray(approvedSelection) &&
    approvedSelection.length > 0
  ) {
    const errMsg =
      "No approved actions could be executed (tools missing from agent config).";
    return {
      run_id: runId,
      agent_id: agentId,
      created_at: nowIso(),
      status: "error",
      results: results.length
        ? results
        : [{ action_id: "", success: false, error: errMsg, data: { error: errMsg } }],
      error: firstError
        ? String(firstError.error || firstError.message || firstError)
        : errMsg,
    };
  }

  for (let si = 0; si < planned.length; si++) {
    const { pair, actionId, proposal_index: pIdx } = planned[si];
    pair.data.agent_run_step_index = si;
    pair.data.agent_run_step_count = nSteps;
    if (si === 0 && reasoningForRun) {
      pair.data.agent_reasoning = reasoningForRun;
    }
    if (si === 0 && bedrockCallsForRun.length) {
      pair.data.bedrock_calls = bedrockCallsForRun;
    }
    try {
      let responseData;
      const isBrowser = isWorkloadBrowserAction(pair.data);
      if (isBrowser) {
        // Bypass API Gateway djangocaller (~29s) — Browser needs up to several minutes.
        responseData = await executeBrowserViaFunctionUrl(pair.data);
        // Fire-and-forget Mongo step log (do not await Browser through Gateway).
        void logBrowserStepViaDjangocaller(pair.data, responseData);
      } else {
        const response = await djangocallerHttpClient.post(
          DJANGOCALLER_PATH,
          { operation: "executeAction", data: pair.data },
          { timeout: pair.data.agent_run_kind === "preview" ? 90000 : 65000 },
        );
        responseData = response.data;
      }
      // Browser + skills/pages consumer return { ok: false, error } on failure.
      // Legacy Lambda integrations often omit ok — treat those as success unless error is set.
      const consumerFailed =
        responseData &&
        typeof responseData === "object" &&
        (responseData.ok === false ||
          (responseData.error &&
            responseData.ok !== true &&
            (String(pair.data?.app || "").includes("workload-") ||
              String(pair.data?.operation || "").startsWith("pages.") ||
              String(pair.data?.operation || "").startsWith("skills."))));
      const success = isBrowser
        ? responseData?.ok === true || responseData?.needsUserAction === true
        : !consumerFailed;
      const base = { action_id: actionId, success, data: responseData };
      if (responseData?.needsUserAction) {
        base.needsUserAction = true;
        base.liveViewUrl = responseData.liveViewUrl || "";
        base.instructions = responseData.instructions || null;
        base.sessionId = responseData.sessionId || "";
        base.profileId = responseData.profileId || "";
        base.viewportWidth = responseData.viewportWidth || responseData.liveViewViewport?.width || 1440;
        base.viewportHeight = responseData.viewportHeight || responseData.liveViewViewport?.height || 900;
        base.liveViewViewport = responseData.liveViewViewport || null;
      }
      if (!success) {
        base.error = responseData;
        firstError = firstError || responseData;
      }
      if (pIdx !== undefined) base.proposal_index = pIdx;
      results.push(base);
    } catch (err) {
      const errPayload = err?.response?.data || { error: err?.message || "Request failed" };
      firstError = firstError || errPayload;
      const base = { action_id: actionId, success: false, error: errPayload };
      if (pIdx !== undefined) base.proposal_index = pIdx;
      results.push(base);
    }
  }

  const errStr = firstError
    ? String(firstError.error || firstError.message || JSON.stringify(firstError))
    : null;

  const anyOk = results.some((r) => r && r.success);
  const anyFail = Boolean(firstError) || results.some((r) => r && r.success === false);
  const status = anyFail && anyOk ? "partial_success" : anyFail ? "error" : "success";

  // Preview has no Celery finalize — stop AgentCore sessions that are not in Live View.
  // Never touch sessions paused for user assist (DCV dies if we stop underneath it).
  try {
    const pausedForLiveView = results.some(
      (r) => r?.needsUserAction || r?.data?.needsUserAction || r?.liveViewUrl || r?.data?.liveViewUrl,
    );
    if (!pausedForLiveView) {
      const sids = harvestBrowserSessionIdsFromResults(results);
      if (sids.length) {
        const stopOut = await stopBrowserSessions(sids);
        console.info("agent preview: stopped browser sessions", sids, stopOut);
      }
    } else {
      console.info("agent preview: keeping browser session(s) for Live View");
    }
  } catch (e) {
    console.warn("agent preview: browser session cleanup failed", e);
  }

  return {
    run_id: runId,
    agent_id: agentId,
    created_at: nowIso(),
    status,
    results,
    error: firstError ? errStr : null,
  };
};

const mockApi = {
  async listAgents() {
    const agents = seedMockAgents();
    return sortByUpdatedAt(agents);
  },

  async getAgent(agentId) {
    const agent = loadAgents().find((item) => item.agent_id === agentId);
    return agent ? clone(agent) : null;
  },

  async createAgent(initialFields = {}) {
    const agents = loadAgents();
    const created = createDefaultAgent(initialFields);
    created.updated_at = nowIso();
    agents.unshift(created);
    saveAgents(agents);
    let out = clone(created);
    try {
      const workflowId = await createWorkflowForAgentApi(created);
      if (workflowId) {
        const list = loadAgents();
        const i = list.findIndex((a) => a.agent_id === created.agent_id);
        if (i >= 0) {
          list[i] = { ...list[i], workflow_id: workflowId };
          saveAgents(list);
          out = clone(list[i]);
        }
      }
    } catch (_) {}
    return out;
  },

  async updateAgent(agentId, patch = {}) {
    const agents = loadAgents();
    const idx = agents.findIndex((item) => item.agent_id === agentId);
    if (idx === -1) throw new Error("Agent not found.");
    const next = sanitizeAgentForSave({
      ...agents[idx],
      ...patch,
      policy: patch.policy ? { ...agents[idx].policy, ...patch.policy } : agents[idx].policy,
      state: patch.state ? { ...agents[idx].state, ...patch.state } : agents[idx].state,
      updated_at: nowIso(),
    });
    agents[idx] = next;
    saveAgents(agents);
    if (next.workflow_id && (patch.triggers != null || patch.actions != null || patch.name != null)) {
      updateWorkflowForAgentApi(next.workflow_id, next).catch(() => {});
    }
    return clone(next);
  },

  async startAgent(agentId, options = null) {
    const onPhase = typeof options?.onPhase === "function" ? options.onPhase : () => {};
    onPhase("activate", "Activating agent…");
    const updated = await mockApi.updateAgent(agentId, { status: "running" });
    if (updated?.workflow_id) {
      onPhase("workflow", "Syncing workflow…");
      await updateWorkflowForAgentApi(updated.workflow_id, updated, true).catch(() => {});
    }
    try {
      const trigger = (updated?.triggers || [])[0];
      if (trigger && agentTriggerNeedsCreateTrigger(trigger)) {
        onPhase("trigger", "Creating trigger…");
      }
      const withTrigger = await registerAgentTriggerIfNeeded(updated);
      if (withTrigger.triggers !== updated.triggers) {
        onPhase("persist_trigger", "Saving trigger…");
        return mockApi.updateAgent(agentId, { triggers: withTrigger.triggers });
      }
      return withTrigger;
    } catch (err) {
      throw new Error(err?.message || "Failed to register agent trigger.");
    }
  },

  async pauseAgent(agentId) {
    const agents = loadAgents();
    const agent = agents.find((item) => item.agent_id === agentId);
    if (agent) await stopAgentTriggerIfNeeded(agent).catch(() => {});
    const updated = await mockApi.updateAgent(agentId, { status: "paused" });
    if (updated?.workflow_id) {
      updateWorkflowForAgentApi(updated.workflow_id, updated, false).catch(() => {});
    }
    return updated;
  },

  async stopAgent(agentId) {
    return mockApi.updateAgent(agentId, { status: "stopped" });
  },

  async deleteAgent(agentId) {
    const agents = loadAgents();
    const filtered = agents.filter((item) => item.agent_id !== agentId);
    if (filtered.length === agents.length) throw new Error("Agent not found.");
    saveAgents(filtered);
    return { deleted: true, agent_id: agentId };
  },

  async cloneAgentToTenants(agentId, tenantIds = []) {
    const source = await mockApi.getAgent(agentId);
    if (!source) throw new Error("Agent not found.");
    const ids = Array.isArray(tenantIds)
      ? [...new Set(tenantIds.map((id) => String(id || "").trim()).filter(Boolean))]
      : [];
    if (ids.length === 0) throw new Error("Select at least one workspace.");
    const results = [];
    for (const tenantId of ids) {
      try {
        const payload = sanitizeAgentForSave(buildClonedAgentPayload(source, tenantId));
        const agents = loadAgents();
        agents.unshift(payload);
        saveAgents(agents);
        let out = clone(payload);
        try {
          const workflowId = await createWorkflowForAgentApi(payload);
          if (workflowId) {
            const list = loadAgents();
            const i = list.findIndex((a) => a.agent_id === payload.agent_id);
            if (i >= 0) {
              list[i] = { ...list[i], workflow_id: workflowId };
              saveAgents(list);
              out = clone(list[i]);
            }
          }
        } catch (_) {}
        results.push({ tenant_id: tenantId, ok: true, agent: out });
      } catch (err) {
        results.push({
          tenant_id: tenantId,
          ok: false,
          error: err?.message || "Clone failed",
        });
      }
    }
    return { source_agent_id: agentId, results };
  },

  async previewAgent(agentId, inputEvent = null, options = null) {
    const onPhase = typeof options?.onPhase === "function" ? options.onPhase : () => {};
    onPhase("load_agent", "Loading agent…");
    const agents = loadAgents();
    const agent = agents.find((item) => item.agent_id === agentId);
    if (!agent) throw new Error("Agent not found.");
    const trigger = (agent.triggers || [])[0];
    onPhase("fetch_sample", "Fetching sample trigger data…");
    const fetchedPayload = trigger
      ? await fetchSampleTriggerDataForAgent(trigger, { workflowId: agent.workflow_id }).catch(() => null)
      : null;
    const triggerEvent = buildTriggerEvent(agent, inputEvent, fetchedPayload);
    const brainResult = await previewAgentWithBrain(agent, triggerEvent, { onPhase });
    if (brainResult && brainResult.from_brain) return brainResult;
    return computeProposedActions(agent, triggerEvent);
  },

  async executeAgentRun(agentId, runId, approvedSelection = null, _agent = null, _proposed = null, _options = null) {
    return mockApi.manualRunAgent(agentId, {
      source: "manual",
      type: "preview_execute",
      payload: { run_id: runId, approved_action_ids: approvedSelection },
    });
  },

  async manualRunAgent(agentId, inputEvent = null) {
    const agents = loadAgents();
    const agent = agents.find((item) => item.agent_id === agentId);
    if (!agent) throw new Error("Agent not found.");

    const runId = `run_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    const referencedActionIds = new Set();
    (agent.rules || []).forEach((rule) => (rule.action_ids || []).forEach((actionId) => referencedActionIds.add(actionId)));
    const actionsTaken = (agent.actions || []).filter((action) => referencedActionIds.has(action.action_id));

    const roll = Math.random();
    const status = roll > 0.25 ? "success" : roll > 0.1 ? "partial_success" : "error";
    const ev = inputEvent || {
      source: "manual",
      type: "debug_run",
      payload: { note: "Manual run from /agents UI" },
    };
    const run_kind =
      ev.type === "preview_execute"
        ? RUN_KINDS.PREVIEW
        : ev.source === "live" || ev.live === true
          ? RUN_KINDS.LIVE
          : RUN_KINDS.MANUAL_TEST;
    const errMsg =
      status === "success"
        ? null
        : status === "partial_success"
          ? "Mock: one tool failed but the run continued."
          : "Mock error: downstream provider timeout.";
    const steps = [];
    let ord = 1;
    steps.push({
      step_id: `${runId}_t${ord}`,
      order: ord++,
      phase: RUN_STEP_PHASES.TRIGGER,
      title: "Run started",
      detail: `Event: ${ev.type || "run"} (${ev.source || "unknown"})`,
      at: nowIso(),
    });
    actionsTaken.forEach((item, idx) => {
      const toolOk =
        status === "success" || (status === "partial_success" && idx < Math.max(1, actionsTaken.length - 1));
      steps.push({
        step_id: `${runId}_t${ord}`,
        order: ord++,
        phase: toolOk ? RUN_STEP_PHASES.TOOL_CALL : RUN_STEP_PHASES.ERROR,
        title: item.label || item.type || "Action",
        tool_label: item.label || item.type,
        app_label: item.app_label || item.app,
        request_summary: "Fields from agent action config sent to executeAction",
        success: toolOk,
        outcome: toolOk ? { ok: true } : { error: errMsg },
        at: nowIso(),
      });
    });
    steps.push({
      step_id: `${runId}_t${ord}`,
      order: ord++,
      phase:
        status === "error"
          ? RUN_STEP_PHASES.ERROR
          : RUN_STEP_PHASES.OUTCOME,
      title:
        status === "success"
          ? "Run finished"
          : status === "partial_success"
            ? "Run finished with partial success"
            : "Run failed",
      detail: errMsg || "Completed.",
      at: nowIso(),
    });
    const run = {
      run_id: runId,
      agent_id: agentId,
      run_kind,
      counts_toward_quota: run_kind === RUN_KINDS.LIVE,
      created_at: nowIso(),
      latency_ms: Math.floor(250 + Math.random() * 800),
      input_event: ev,
      summary:
        status === "partial_success"
          ? "Run finished with partial success (some tools failed)."
          : run_kind === RUN_KINDS.PREVIEW
            ? "Preview: approved actions executed"
            : "Manual test run from editor",
      actions_taken: actionsTaken.map((item) => ({
        action_id: item.action_id,
        type: item.type,
        label: item.label || item.type,
        app: item.app,
        app_label: item.app_label,
        app_icon: item.app_icon,
      })),
      outputs: status === "error" ? null : { result: "Rules evaluated and actions executed." },
      error: errMsg,
      status,
      steps,
    };

    const runsByAgent = loadRuns();
    runsByAgent[agentId] = [run, ...(runsByAgent[agentId] || [])];
    saveRuns(runsByAgent);

    const nextErrorStreak = status === "error" ? (agent.state?.error_streak || 0) + 1 : 0;
    const shouldKillSwitch = nextErrorStreak >= (agent.policy?.kill_switch_error_streak || 5);
    const patchedAgent = {
      ...agent,
      status: shouldKillSwitch ? "error" : agent.status,
      state: {
        ...(agent.state || {}),
        error_streak: nextErrorStreak,
        last_error: run.error,
        last_event_at: run.created_at,
        last_tick_at: run.created_at,
      },
      updated_at: nowIso(),
    };

    const idx = agents.findIndex((item) => item.agent_id === agentId);
    agents[idx] = patchedAgent;
    saveAgents(agents);
    return run;
  },

  async listAgentRunsPage(agentId, query = {}) {
    const runsByAgent = loadRuns();
    const all = clone(runsByAgent[agentId] || []);
    let out = [...all];
    const status = query.status;
    if (status && status !== "all") {
      out = out.filter((r) => String(r.status || "").toLowerCase() === String(status).toLowerCase());
    }
    if (query.dateFrom) {
      const t0 = new Date(`${query.dateFrom}T00:00:00`).getTime();
      out = out.filter((r) => (r.created_at ? new Date(r.created_at).getTime() : 0) >= t0);
    }
    if (query.dateTo) {
      const t1 = new Date(`${query.dateTo}T23:59:59.999`).getTime();
      out = out.filter((r) => (r.created_at ? new Date(r.created_at).getTime() : 0) <= t1);
    }
    if (query.app && query.app !== "all") {
      const want = String(query.app).toLowerCase();
      out = out.filter((run) => {
        const steps = run.steps || [];
        return steps.some((s) => String(s.app_label || s.app || "").toLowerCase() === want);
      });
    }
    if (query.q != null && String(query.q).trim()) {
      const q = String(query.q).trim().toLowerCase();
      out = out.filter((run) => {
        let blob = "";
        try {
          blob = JSON.stringify({
            run_id: run.run_id,
            task_id: run.task_id,
            status: run.status,
            summary: run.summary,
            run_kind: run.run_kind,
            error: run.error,
          }).toLowerCase();
        } catch {
          blob = String(run.run_id || run.task_id || "").toLowerCase();
        }
        return blob.includes(q);
      });
    }
    const limit = Number.isFinite(query.limit) && query.limit > 0 ? query.limit : 25;
    const skip = Number.isFinite(query.skip) && query.skip >= 0 ? query.skip : 0;
    const total = out.length;
    const liveTotal = out.filter((r) => r.run_kind === "live").length;
    const runs = out.slice(skip, skip + limit);
    return { runs, total, liveTotal };
  },

  async getAgentUsage() {
    const tenant = await fetchTenantPlan();
    const period = billingPeriodQuery(tenant);
    const lifetime = isLifetimeTaskQuota(tenant);
    const agents = loadAgents();
    const runsByAgent = loadRuns();
    const byAgent = (agents || []).map((agent) => {
      const list = runsByAgent[agent.agent_id] || [];
      const live_total = list.reduce((sum, r) => {
        if (!(r?.run_kind === RUN_KINDS.LIVE || r?.run_kind === "live")) return sum;
        if (!lifetime && !runCreatedAtInPeriod(r, period.periodStart, period.periodEnd)) return sum;
        return sum + (runActionCount(r) || 0);
      }, 0);
      return { agent_id: agent.agent_id, name: agent.name || "Untitled Agent", live_total };
    }).sort((a, b) => b.live_total - a.live_total);
    const liveTotal = byAgent.reduce((sum, row) => sum + row.live_total, 0);
    return buildUsageSummary({ tenant, liveTotal, byAgent, period });
  },

  async listAgentRuns(agentId) {
    const { runs } = await this.listAgentRunsPage(agentId, { limit: 20000, skip: 0 });
    return runs;
  },

  async getAgentRunDetail(agentId, runId) {
    const runsByAgent = loadRuns();
    const list = runsByAgent[agentId] || [];
    const found = list.find((r) => r && r.run_id === runId);
    return found ? clone(found) : null;
  },

  async generateAgentRunRecap(agentId, runId, options = {}) {
    const agents = loadAgents();
    const agent = agents.find((item) => item.agent_id === agentId);
    const goal = (agent?.goal || "").slice(0, 200);
    return {
      recap: `This is a mock AI recap (enable the Django djangocaller + Bedrock for live summaries). Agent goal: ${goal || "—"}. Run ${runId} completed with the steps shown in the timeline.`,
    };
  },
};

/**
 * Prefer POST /agents/:id/start|pause|stop. If the server has not implemented those yet (404/405/501),
 * fall back to PATCH { status } so Express CRUD-only backends still work during rollout.
 */
const postLifecycleOrPatchStatus = async (agentId, pathSuffix, status) => {
  try {
    const response = await httpClient.post(`/agents/${agentId}${pathSuffix}`);
    const out = normalizeAgentFromApi(response.data);
    return out ?? (await refetchAgentRecord(agentId));
  } catch (err) {
    const code = err?.response?.status;
    if (code === 404 || code === 405 || code === 501) {
      await httpClient.patch(`/agents/${agentId}`, { status });
      const u = await refetchAgentRecord(agentId);
      if (u) return u;
    }
    throw err;
  }
};

const httpApi = {
  async listAgents() {
    const response = await httpClient.get("/agents");
    const arr = normalizeAgentListPayload(response.data);
    return arr.map((x) => normalizeAgentFromApi(x)).filter(Boolean);
  },
  async getAgent(agentId) {
    const response = await httpClient.get(`/agents/${agentId}`);
    return normalizeAgentFromApi(response.data);
  },
  async createAgent(initialFields = {}) {
    const payload = createDefaultAgent(initialFields);
    const response = await httpClient.post("/agents", payload);
    let agent = normalizeAgentFromApi(response.data);
    if (!agent?.agent_id) {
      throw new Error("Create agent response missing agent_id. Return the full document (or _id).");
    }
    try {
      const workflowId = await createWorkflowForAgentApi(agent);
      if (workflowId) {
        await httpClient.patch(`/agents/${agent.agent_id}`, { workflow_id: workflowId });
        agent = (await refetchAgentRecord(agent.agent_id)) ?? { ...agent, workflow_id: workflowId };
      }
    } catch (_) {}
    return agent;
  },
  async updateAgent(agentId, patch = {}) {
    await httpClient.patch(`/agents/${agentId}`, patch);
    const updated = await refetchAgentRecord(agentId);
    if (!updated) {
      throw new Error("Failed to load agent after save. Check GET /agents/:id returns the full document.");
    }
    if (updated?.workflow_id && (patch.triggers != null || patch.actions != null || patch.name != null)) {
      // Must mirror start/pause: saving config used to pass setActive=false and cleared workflowTemplate.active
      // while the agent stayed "running", so triggers/orchestrator stopped after any save (e.g. back navigation).
      const syncWorkflowActive = String(updated.status || "").toLowerCase() === "running";
      updateWorkflowForAgentApi(updated.workflow_id, updated, syncWorkflowActive).catch(() => {});
    }
    return updated;
  },
  async startAgent(agentId, options = null) {
    const onPhase = typeof options?.onPhase === "function" ? options.onPhase : () => {};
    onPhase("activate", "Activating agent…");
    let updated = await postLifecycleOrPatchStatus(agentId, "/start", "running");
    if (updated?.workflow_id) {
      onPhase("workflow", "Syncing workflow…");
      await updateWorkflowForAgentApi(updated.workflow_id, updated, true).catch(() => {});
    }
    try {
      const trigger = (updated?.triggers || [])[0];
      if (trigger && agentTriggerNeedsCreateTrigger(trigger)) {
        onPhase("trigger", "Creating trigger…");
      }
      const withTrigger = await registerAgentTriggerIfNeeded(updated);
      if (withTrigger.triggers !== updated.triggers) {
        onPhase("persist_trigger", "Saving trigger…");
        updated = await httpApi.updateAgent(agentId, { triggers: withTrigger.triggers });
      } else {
        updated = withTrigger;
      }
    } catch (err) {
      throw new Error(err?.message || "Failed to register agent trigger.");
    }
    return updated;
  },
  async pauseAgent(agentId) {
    const agent = await refetchAgentRecord(agentId).catch(() => null);
    if (agent) await stopAgentTriggerIfNeeded(agent).catch(() => {});
    const updated = await postLifecycleOrPatchStatus(agentId, "/pause", "paused");
    if (updated?.workflow_id) {
      updateWorkflowForAgentApi(updated.workflow_id, updated, false).catch(() => {});
    }
    return updated;
  },
  async stopAgent(agentId) {
    return postLifecycleOrPatchStatus(agentId, "/stop", "stopped");
  },
  async deleteAgent(agentId) {
    await httpClient.delete(`/agents/${agentId}`);
    return { deleted: true, agent_id: agentId };
  },
  async cloneAgentToTenants(agentId, tenantIds = []) {
    const source = await httpApi.getAgent(agentId);
    if (!source?.agent_id) throw new Error("Agent not found.");
    const ids = Array.isArray(tenantIds)
      ? [...new Set(tenantIds.map((id) => String(id || "").trim()).filter(Boolean))]
      : [];
    if (ids.length === 0) throw new Error("Select at least one workspace.");
    const results = [];
    for (const tenantId of ids) {
      try {
        const payload = sanitizeAgentForSave(buildClonedAgentPayload(source, tenantId));
        const response = await httpClient.post("/agents", payload, {
          headers: { "X-Tenant-Id": tenantId },
        });
        let agent = normalizeAgentFromApi(response.data);
        if (!agent?.agent_id) {
          throw new Error("Create agent response missing agent_id.");
        }
        try {
          const workflowId = await createWorkflowForAgentApi(agent);
          if (workflowId) {
            await httpClient.patch(
              `/agents/${agent.agent_id}`,
              { workflow_id: workflowId },
              { headers: { "X-Tenant-Id": tenantId } },
            );
            agent = { ...agent, workflow_id: workflowId };
          }
        } catch (_) {}
        results.push({ tenant_id: tenantId, ok: true, agent });
      } catch (err) {
        results.push({
          tenant_id: tenantId,
          ok: false,
          error: err?.response?.data?.error || err?.message || "Clone failed",
        });
      }
    }
    return { source_agent_id: agentId, results };
  },
  async previewAgent(agentId, inputEvent = null, options = null) {
    const onPhase = typeof options?.onPhase === "function" ? options.onPhase : () => {};
    onPhase("load_agent", "Loading agent…");
    let agent = await httpClient
      .get(`/agents/${agentId}`)
      .then((r) => normalizeAgentFromApi(r.data))
      .catch(() => null);
    if (!agent) agent = loadAgents().find((item) => item.agent_id === agentId) || null;
    if (!agent) throw new Error("Agent not found.");
    const trigger = (agent.triggers || [])[0];
    onPhase("fetch_sample", "Fetching sample trigger data…");
    const fetchedPayload = trigger
      ? await fetchSampleTriggerDataForAgent(trigger, { workflowId: agent.workflow_id }).catch(() => null)
      : null;
    const triggerEvent = buildTriggerEvent(agent, inputEvent, fetchedPayload);
    const brainResult = await previewAgentWithBrain(agent, triggerEvent, { onPhase });
    if (brainResult && brainResult.from_brain) return brainResult;
    return computeProposedActions(agent, triggerEvent);
  },
  async executeAgentRun(agentId, runId, approvedSelection = null, agent = null, proposedActionsFromPreview = null, options = null) {
    return executeViaDjangocaller(agentId, runId, approvedSelection, agent, proposedActionsFromPreview, options);
  },
  async manualRunAgent(agentId, inputEvent = {}) {
    try {
      const response = await httpClient.post(`/agents/${agentId}/run`, inputEvent);
      return response.data;
    } catch (err) {
      const code = err?.response?.status;
      if (code === 404 || code === 405 || code === 501) {
        throw new Error("Run is not implemented on the server yet.");
      }
      throw err;
    }
  },
  async listAgentRunsPage(agentId, query = {}) {
    const qRaw = query?.q != null ? String(query.q).trim() : "";
    try {
      const params = buildAgentRunsQueryParams(query);
      const response = await httpClient.get(`/agents/${agentId}/runs`, { params });
      const page = normalizeRunListPageResponse(response.data);
      // If the backend only searches shallow fields, common IDs inside step outcomes won't match.
      // Fall back to client-side deep search over a wider window.
      if (qRaw && (!page?.runs || page.runs.length === 0)) {
        const wideLimit = Math.max(500, Number.isFinite(query.limit) ? query.limit : 500);
        const wideSkip = 0;
        const paramsWide = buildAgentRunsQueryParams({
          ...query,
          q: "",
          limit: wideLimit,
          skip: wideSkip,
        });
        const wideRes = await httpClient.get(`/agents/${agentId}/runs`, { params: paramsWide });
        const widePage = normalizeRunListPageResponse(wideRes.data);
        const filtered = _clientFilterRunsPage(widePage?.runs || [], query);
        const limit = Number.isFinite(query.limit) && query.limit > 0 ? query.limit : 25;
        const skip = Number.isFinite(query.skip) && query.skip >= 0 ? query.skip : 0;
        const total = filtered.length;
        const liveTotal = filtered.filter((r) => r?.run_kind === RUN_KINDS.LIVE || r?.run_kind === "live").length;
        return { runs: filtered.slice(skip, skip + limit), total, liveTotal };
      }
      return page;
    } catch {
      // Even when the server errors, try the same wide + client-side filtering if search is present.
      if (qRaw) {
        try {
          const wideLimit = Math.max(500, Number.isFinite(query.limit) ? query.limit : 500);
          const paramsWide = buildAgentRunsQueryParams({ ...query, q: "", limit: wideLimit, skip: 0 });
          const wideRes = await httpClient.get(`/agents/${agentId}/runs`, { params: paramsWide });
          const widePage = normalizeRunListPageResponse(wideRes.data);
          const filtered = _clientFilterRunsPage(widePage?.runs || [], query);
          const limit = Number.isFinite(query.limit) && query.limit > 0 ? query.limit : 25;
          const skip = Number.isFinite(query.skip) && query.skip >= 0 ? query.skip : 0;
          const total = filtered.length;
          const liveTotal = filtered.filter((r) => r?.run_kind === RUN_KINDS.LIVE || r?.run_kind === "live").length;
          return { runs: filtered.slice(skip, skip + limit), total, liveTotal };
        } catch {
          // ignore
        }
      }
      return { runs: [], total: 0, liveTotal: null };
    }
  },

  async listAgentRuns(agentId) {
    const { runs } = await httpApi.listAgentRunsPage(agentId, { limit: 500, skip: 0 });
    return runs;
  },

  /**
   * @param {{ allowPerAgentFanOut?: boolean }} [options]
   *   allowPerAgentFanOut — when true (default), may page GET /agents/:id/runs for every agent
   *   if GET /agents/usage is unavailable. Pass false on the /agents list so the page stays fast.
   */
  async getAgentUsage(options = {}) {
    const allowPerAgentFanOut = options?.allowPerAgentFanOut !== false;
    const tenant = await fetchTenantPlan();
    const period = billingPeriodQuery(tenant);
    const lifetime = isLifetimeTaskQuota(tenant);

    // Prefer the aggregate endpoint — avoids N× GET /agents/:id/runs on the /agents list page.
    // Free plans are lifetime credits, so do not window the breakdown to this month.
    try {
      const response = await httpClient.get("/agents/usage", {
        params: lifetime
          ? {}
          : {
              date_from: period.dateFrom,
              date_to: period.dateTo,
            },
        timeout: 15000,
      });
      const data = response?.data && typeof response.data === "object" ? response.data : {};
      const rawRows = Array.isArray(data.by_agent)
        ? data.by_agent
        : Array.isArray(data.byAgent)
          ? data.byAgent
          : [];
      const liveTotal = Number(data.live_total ?? data.liveTotal);
      if (Number.isFinite(liveTotal)) {
        const byAgent = rawRows
          .map((row) => ({
            agent_id: row?.agent_id || row?.agentId || "",
            name: row?.name || "Untitled Agent",
            live_total: Number(row?.live_total ?? row?.liveTotal) || 0,
          }))
          .filter((row) => row.agent_id)
          .sort((a, b) => b.live_total - a.live_total);
        return buildUsageSummary({ tenant, liveTotal, byAgent, period });
      }
    } catch {
      // Fall through when the usage route is missing/old.
    }

    // Fast path for the agents list: tenant creditUsage is incremented on live finalize.
    if (!allowPerAgentFanOut) {
      const creditUsage = lifetime ? resolveTaskUsage(tenant) : Number(tenant?.creditUsage);
      const liveTotal = Number.isFinite(creditUsage) && creditUsage >= 0 ? creditUsage : 0;
      return buildUsageSummary({ tenant, liveTotal, byAgent: [], period });
    }

    const countViaPerAgent = async () => {
      const agents = await httpApi.listAgents().catch(() => []);
      const periodStartMs =
        period.periodStart instanceof Date ? period.periodStart.getTime() : Number(period.periodStart);
      const rows = await Promise.all(
        (agents || []).map(async (agent) => {
          const id = agent?.agent_id;
          if (!id) return null;
          try {
            // Sum tool calls (Actions), not run documents. Page newest-first until
            // we pass the billing-window start. Prefer date filters to shrink payloads.
            // Free plans count every live run — the allowance does not reset monthly.
            const pageSize = 200;
            const maxPages = lifetime ? 80 : 40;
            let skip = 0;
            let liveInPeriod = 0;
            for (let p = 0; p < maxPages; p += 1) {
              const page = await httpApi.listAgentRunsPage(id, {
                limit: pageSize,
                skip,
                ...(lifetime
                  ? {}
                  : {
                      dateFrom: period.dateFrom,
                      dateTo: period.dateTo,
                    }),
              });
              const runs = page?.runs || [];
              if (runs.length === 0) break;
              for (const r of runs) {
                if (!(r?.run_kind === RUN_KINDS.LIVE || r?.run_kind === "live")) continue;
                if (!lifetime && !runCreatedAtInPeriod(r, period.periodStart, period.periodEnd)) continue;
                liveInPeriod += runActionCount(r) || 0;
              }
              skip += runs.length;
              if (runs.length < pageSize) break;
              if (!lifetime) {
                const allBeforePeriod = runs.every((r) => {
                  const t = parseRunCreatedAtMs(r?.created_at);
                  return Number.isFinite(t) && Number.isFinite(periodStartMs) && t < periodStartMs;
                });
                if (allBeforePeriod) break;
              }
            }
            return {
              agent_id: id,
              name: agent.name || "Untitled Agent",
              live_total: liveInPeriod,
            };
          } catch {
            return { agent_id: id, name: agent.name || "Untitled Agent", live_total: 0 };
          }
        }),
      );
      const byAgent = rows.filter(Boolean).sort((a, b) => b.live_total - a.live_total);
      const liveTotal = byAgent.reduce((sum, row) => sum + (Number(row.live_total) || 0), 0);
      return { liveTotal, byAgent };
    };

    const { liveTotal, byAgent } = await countViaPerAgent();
    return buildUsageSummary({ tenant, liveTotal, byAgent, period });
  },

  async generateAgentRunRecap(agentId, runId, options = {}) {
    const force = Boolean(options.force);
    const collection = config.AGENT_RUN_MONGO_COLLECTION || "agent_tasks";
    const payload = {
      operation: "generateAgentRunRecap",
      data: {
        agent_id: agentId,
        run_id: runId,
        agent_task_id: runId,
        force,
        agent_run_collection: collection,
      },
    };
    const res = await djangocallerHttpClient.post(DJANGOCALLER_PATH, payload, { timeout: 120000 });
    const data = res.data;
    if (data && data.error) {
      throw new Error(typeof data.error === "string" ? data.error : data.detail || "Recap failed");
    }
    return { recap: data?.recap || "" };
  },

  async getAgentRunDetail(agentId, runId) {
    try {
      const response = await httpClient.get(`/agents/${agentId}/runs/${runId}`);
      return normalizeRunFromApi(response.data);
    } catch {
      const want = String(runId);
      const { runs } = await httpApi.listAgentRunsPage(agentId, {
        limit: 500,
        skip: 0,
        q: want,
      });
      const fromQ =
        runs.find((r) => {
          if (!r) return false;
          if (r.run_id != null && String(r.run_id) === want) return true;
          if (r.task_id != null && String(r.task_id) === want) return true;
          return false;
        }) || null;
      if (fromQ) return fromQ;
      const { runs: wide } = await httpApi.listAgentRunsPage(agentId, { limit: 500, skip: 0 });
      return (
        wide.find((r) => {
          if (!r) return false;
          if (r.run_id != null && String(r.run_id) === want) return true;
          if (r.task_id != null && String(r.task_id) === want) return true;
          return false;
        }) || null
      );
    }
  },
};

const getApi = () => (getMode() === "http" ? httpApi : mockApi);

export const listAgents = (...args) => getApi().listAgents(...args);
export const getAgent = (...args) => getApi().getAgent(...args);
export const createAgent = (...args) => getApi().createAgent(...args);
export const updateAgent = (...args) => getApi().updateAgent(...args);
export const startAgent = (...args) => getApi().startAgent(...args);
export const pauseAgent = (...args) => getApi().pauseAgent(...args);
export const stopAgent = (...args) => getApi().stopAgent(...args);
export const deleteAgent = (...args) => getApi().deleteAgent(...args);
export const cloneAgentToTenants = (...args) => getApi().cloneAgentToTenants(...args);

/** Create one-time share invite(s) and email recipient(s). */
export const shareAgentByEmail = async (agentId, { email, emails, sharerName } = {}) => {
  const list = [];
  if (Array.isArray(emails)) list.push(...emails);
  if (email != null && String(email).trim()) list.push(email);
  const unique = [
    ...new Set(list.map((e) => String(e || "").trim().toLowerCase()).filter(Boolean)),
  ];
  const response = await httpClient.post(`/agents/${agentId}/share`, {
    emails: unique,
    sharer_name: sharerName || undefined,
  });
  return response.data;
};

/** Public share metadata (no auth required for the landing page). */
export const getAgentShare = async (shareToken) => {
  const response = await httpClient.get(`/agents/share/${encodeURIComponent(shareToken)}`);
  return response.data;
};

/**
 * Accept share into a destination tenant. Clones live source on the server (scrubbed).
 * Temporarily sets X-Tenant-Id to the destination workspace.
 */
export const acceptAgentShare = async (shareToken, tenantId) => {
  const response = await httpClient.post(
    `/agents/share/${encodeURIComponent(shareToken)}/accept`,
    { tenant_id: tenantId },
    { headers: { "X-Tenant-Id": String(tenantId || "").trim() } },
  );
  return response.data;
};

/** Workspaces the signed-in user can access (excluding current tenant by default). */
export const listAccessibleTenants = async ({ excludeCurrent = true } = {}) => {
  let tok = "";
  let currentTenant = "";
  try {
    tok = String(localStorage.getItem("tok_") || "").trim();
    currentTenant = String(localStorage.getItem("tenant") || "").trim();
  } catch (_) {
    /* ignore */
  }
  if (!tok) return [];
  const response = await axios.get("https://backend.workload.co/api/auth/me", {
    headers: { Authorization: `Bearer ${tok}` },
  });
  const tenants = Array.isArray(response?.data?.tenants) ? response.data.tenants : [];
  return tenants
    .map((entry) => {
      const id = entry?.tenant?.id ?? entry?.id;
      if (id == null || String(id).trim() === "") return null;
      const name = entry?.tenant?.name || entry?.name || `Workspace ${id}`;
      return { id: String(id), name: String(name) };
    })
    .filter(Boolean)
    .filter((t) => !(excludeCurrent && currentTenant && t.id === currentTenant));
};
export const previewAgent = (...args) => getApi().previewAgent(...args);
export const executeAgentRun = async (agentId, runId, approvedSelection, agent, proposedActionsFromPreview, options = null) => {
  if (isExecuteViaHttp()) {
    return executeViaDjangocaller(agentId, runId, approvedSelection, agent, proposedActionsFromPreview, options);
  }
  return getApi().executeAgentRun(agentId, runId, approvedSelection, agent, proposedActionsFromPreview, options);
};
export const manualRunAgent = (...args) => getApi().manualRunAgent(...args);
export const listAgentRuns = (...args) => getApi().listAgentRuns(...args);
export const listAgentRunsPage = (...args) => getApi().listAgentRunsPage(...args);
export const getAgentUsage = (...args) => getApi().getAgentUsage(...args);

export async function fetchAiUsage() {
  const tenant = await fetchTenantPlan();
  const period = billingPeriodQuery(tenant);
  const response = await httpClient.get("/agents/ai-usage", {
    params: {
      date_from: period.dateFrom,
      date_to: period.dateTo,
    },
    timeout: 15000,
  });
  const data = response?.data && typeof response.data === "object" ? response.data : {};
  return {
    ...data,
    periodStart: period.periodStart,
    periodEnd: period.periodEnd,
  };
}
export const getAgentRunDetail = (...args) => getApi().getAgentRunDetail(...args);
export const generateAgentRunRecap = (...args) => getApi().generateAgentRunRecap(...args);
